import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  Search, AlertTriangle, CheckCircle, ChevronDown, ChevronRight,
  FileText, Database, Clock, BookOpen, Target, Lightbulb, Wrench,
  AlertCircle, Info, Shield, User, Calendar, Layers, Tag, Paperclip,
  Loader2, MessageSquare, Save, Trash2, HelpCircle, Settings2,
} from 'lucide-react';
import { MarkdownRenderer } from '../common/MarkdownRenderer';
import { HintCaptureMenu } from './HintCaptureMenu';
import { EntityToolsPanel } from './EntityToolsPanel';
import { SqlInvestigation, seedCardsFromResult, CardBlock, type SqlCard } from './SqlInvestigation';
import { AnalysisFlowchart } from './AnalysisFlowchart';
import { FlowchartModal } from './FlowchartModal';
import { ModuleGraphModal } from './ModuleGraphModal';
import { ApplicableSqlScripts } from './ApplicableSqlScripts';
import { RcaVisualizationPanel } from './RcaVisualizationPanel';
import { LayeredRcaCards } from './LayeredRcaCards';
import { CompassImpactPanel } from './CompassImpactPanel';
import { CompassSearchBrowse } from './CompassSearchBrowse';
import { buildDataContextWithEvidence, buildHints, buildTicketText } from '../../services/compassApi';
import { mapProgressToStepId } from '../../constants/analysisSteps';
import { downloadAnalysisJson } from '../../utils/downloadJson';
import { CoverageNotice, CoverageItem, attachmentCoverage } from '../common/CoverageNotice';
import { emailName } from '../../utils/dateFormat';
import { issueAnalysisApi } from '../../services/issueAnalysisApi';
import { ApiError } from '../../services/api';
import type {
  IssueAnalysisResponse, ExtractedEntity, EntityBinding, DiagnosisHypothesis,
  DataGap, ConfidenceFlag, SqlDataResult, StructuredTicket, ModuleTable,
  TableRecommendation, TableField,
} from '../../services/issueAnalysisApi';

/** Colour mapping for confidence flags. */
function confidenceBadge(level: ConfidenceFlag) {
  const styles: Record<ConfidenceFlag, string> = {
    high: 'bg-green-100 text-green-700 border-green-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-red-100 text-red-700 border-red-200',
  };
  return (
    <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border ${styles[level]}`}>
      {level}
    </span>
  );
}

/** Collapsible section wrapper. */
function Section({
  title, icon, defaultOpen = false, children, badge,
}: {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
  badge?: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors bg-gray-50/50"
      >
        {open ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
        {icon}
        <span className="text-xs font-semibold text-gray-800 flex-1">{title}</span>
        {badge}
      </button>
      {open && <div className="px-3 pb-3 pt-2 border-t border-gray-100 space-y-2">{children}</div>}
    </div>
  );
}

/** Entity extraction table. With `actions` set, values are click-to-edit,
 *  rows removable, and a new entity can be added. */
export interface EntityTableActions {
  onEditValue: (index: number, newValue: string) => void;
  onRemove: (index: number) => void;
  onAdd: (entityType: string, value: string) => void;
  /** Vouch for a low-confidence value permanently, not just for this ticket. */
  onConfirm?: (index: number) => void;
}

function EditableValueCell({ value, onCommit }: { value: string; onCommit: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        title="Click to edit value"
        className="font-mono text-blue-700 underline decoration-dashed hover:bg-blue-50 rounded px-0.5"
      >
        {value}
      </button>
    );
  }
  const commit = () => {
    const t = draft.trim();
    if (t && t !== value) onCommit(t); else setDraft(value);
    setEditing(false);
  };
  return (
    <input
      type="text"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value); setEditing(false); } }}
      autoFocus
      className="font-mono text-xs border border-blue-400 rounded px-1 py-0 w-40 focus:outline-none"
    />
  );
}

function EntityTable({ entities, actions }: { entities: ExtractedEntity[]; actions?: EntityTableActions }) {
  const [newType, setNewType] = useState('');
  const [newValue, setNewValue] = useState('');
  if ((!entities || entities.length === 0) && !actions) {
    return <p className="text-xs text-gray-500 italic">No entities extracted</p>;
  }
  const add = () => {
    if (!actions || !newValue.trim()) return;
    actions.onAdd(newType.trim() || 'Identifier', newValue.trim());
    setNewValue('');
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200 text-left">
            <th scope="col" className="py-1 pr-2 font-semibold text-gray-600">Type</th>
            <th scope="col" className="py-1 pr-2 font-semibold text-gray-600">Value</th>
            <th scope="col" className="py-1 pr-2 font-semibold text-gray-600">Confidence</th>
            <th scope="col" className="py-1 font-semibold text-gray-600">Reason</th>
            {actions && <th scope="col" className="py-1 w-6" aria-label="Remove" />}
          </tr>
        </thead>
        <tbody>
          {entities.map((e, i) => (
            <tr key={`${e.entity_type}-${e.value}`} className="border-b border-gray-100">
              <td className="py-1 pr-2 font-medium text-gray-800">{e.entity_type}</td>
              <td className="py-1 pr-2 font-mono text-blue-700">
                {actions ? (
                  <EditableValueCell value={e.value} onCommit={(v) => actions.onEditValue(i, v)} />
                ) : (
                  e.value
                )}
              </td>
              <td className="py-1 pr-2">{confidenceBadge(e.confidence)}</td>
              <td className="py-1 text-gray-500">{e.confidence_reason}</td>
              {actions && (
                <td className="py-1 text-right whitespace-nowrap">
                  {/* Confirm sits beside Remove: both are verdicts on this
                      row, so they belong together. Offered only where there
                      is doubt to resolve — a tick on a HIGH value would
                      invite confirming what nobody questioned, and dilute
                      what a confirmation means. */}
                  {e.confidence === 'low' && (
                    <button
                      onClick={() => actions.onConfirm?.(i)}
                      title="I know this value is correct — remember it, so later tickets do not treat it as uncertain"
                      className="text-green-600 hover:text-green-800 font-bold px-1 rounded hover:bg-green-50"
                    >
                      ✓
                    </button>
                  )}
                  <button
                    onClick={() => actions.onRemove(i)}
                    title="Remove entity"
                    className="text-red-400 hover:text-red-700 font-bold px-1"
                  >
                    &times;
                  </button>
                </td>
              )}
            </tr>
          ))}
          {actions && (
            <tr>
              <td className="py-1 pr-2">
                <input
                  type="text"
                  value={newType}
                  onChange={(e) => setNewType(e.target.value)}
                  placeholder="Type (e.g. MRG)"
                  className="border border-gray-300 rounded px-1 py-0.5 text-xs w-32"
                />
              </td>
              <td className="py-1 pr-2">
                <input
                  type="text"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && add()}
                  placeholder="Value"
                  className="border border-gray-300 rounded px-1 py-0.5 text-xs font-mono w-40"
                />
              </td>
              <td colSpan={3} className="py-1">
                <button
                  onClick={add}
                  disabled={!newValue.trim()}
                  className="text-[10px] px-2 py-0.5 text-blue-600 border border-blue-300 rounded hover:bg-blue-50 disabled:opacity-40"
                >
                  + Add entity
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Hypothesis card. */
function HypothesisCard({ h, index }: { h: DiagnosisHypothesis; index: number }) {
  const [expanded, setExpanded] = useState(index === 0);
  const pct = Math.round(h.confidence_score * 100);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} Hypothesis ${index + 1}`}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
      >
        {expanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
        <Lightbulb size={14} className="text-amber-500 flex-shrink-0" />
        <span className="text-xs font-semibold text-gray-800 flex-1">
          Hypothesis {index + 1}
        </span>
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded"
          style={{
            background: pct >= 70 ? '#dcfce7' : pct >= 50 ? '#fef9c3' : '#f3f4f6',
            color: pct >= 70 ? '#15803d' : pct >= 50 ? '#a16207' : '#6b7280',
          }}
        >
          {pct}%
        </span>
        {confidenceBadge(h.confidence)}
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-gray-100 pt-2">
          <p className="text-xs text-gray-700 leading-relaxed">{h.statement}</p>
          {h.supporting_evidence.length > 0 && (
            <div>
              <span className="text-[10px] font-semibold text-green-600 uppercase tracking-wide">Supporting Evidence</span>
              <ul className="mt-1 space-y-0.5">
                {h.supporting_evidence.map((e, i) => (
                  <li key={`support-${i}-${e.slice(0, 20)}`} className="text-[10px] text-gray-600 pl-2 border-l-2 border-green-200">{e}</li>
                ))}
              </ul>
            </div>
          )}
          {h.contradicting_evidence.length > 0 && (
            <div>
              <span className="text-[10px] font-semibold text-red-600 uppercase tracking-wide">Contradicting Evidence</span>
              <ul className="mt-1 space-y-0.5">
                {h.contradicting_evidence.map((e, i) => (
                  <li key={`contradict-${i}-${e.slice(0, 20)}`} className="text-[10px] text-gray-600 pl-2 border-l-2 border-red-200">{e}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Warning / gap display. */
function GapWarning({ gap }: { gap: DataGap }) {
  return (
    <div className="flex items-start gap-2 p-2 bg-amber-50 rounded border border-amber-200 text-xs">
      <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
      <div>
        <p className="font-semibold text-amber-800">No data found: {gap.looked_for}</p>
        <p className="text-amber-700">Searched in: {gap.searched_in}</p>
        <p className="text-amber-600 italic">Action: {gap.recommended_action}</p>
      </div>
    </div>
  );
}

/** Renders a JSON array of objects as an HTML table. */
/** Render a single result table */
function SimpleTable({ rows, maxRows = 25 }: { rows: Record<string, unknown>[]; maxRows?: number }) {
  if (rows.length === 0) return null;
  const columns = Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[10px] border-collapse">
        <thead>
          <tr className="border-b border-gray-300 text-left">
            {columns.map((col) => (
              <th key={`col-${col}`} scope="col" className="py-1 px-1.5 font-semibold text-gray-600 bg-gray-100 whitespace-nowrap">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, maxRows).map((row, i) => (
            <tr key={`row-${i}`} className="border-b border-gray-100 hover:bg-blue-50/30">
              {columns.map((col) => (
                <td key={`cell-${col}`} className="py-0.5 px-1.5 text-gray-700 whitespace-nowrap max-w-[200px] truncate">
                  {row[col] == null ? <span className="text-gray-300 italic">null</span> : String(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > maxRows && (
        <p className="mt-0.5 text-[10px] text-gray-400 italic">Showing {maxRows} of {rows.length} rows</p>
      )}
    </div>
  );
}

function EraxDataTable({ data }: { data: unknown }) {
  if (!data || !Array.isArray(data) || data.length === 0) return null;

  // New per-table format: [{ table, row_count, rows }, ...]
  const first = data[0] as Record<string, unknown>;
  if (first && 'table' in first && 'rows' in first && Array.isArray(first.rows)) {
    const tableResults = data as { table: string; row_count: number; rows: Record<string, unknown>[] }[];
    return (
      <div className="space-y-3 mt-1">
        {tableResults.map((tr, idx) => (
          <div key={`table-${idx}`}>
            <div className="text-[10px] font-semibold text-indigo-700 mb-0.5">
              {tr.table} <span className="font-normal text-gray-400">({tr.row_count} row{tr.row_count !== 1 ? 's' : ''})</span>
            </div>
            <SimpleTable rows={tr.rows} />
          </div>
        ))}
      </div>
    );
  }

  // Legacy flat format
  return <div className="mt-1"><SimpleTable rows={data as Record<string, unknown>[]} maxRows={50} /></div>;
}

/** SQL result card with optional ERAX live data. */
function SqlResultCard({ result: r }: { result: SqlDataResult }) {
  const [sqlExpanded, setSqlExpanded] = useState(false);
  const [dataExpanded, setDataExpanded] = useState(false);
  const hasEraxData = r.erax_data && (Array.isArray(r.erax_data) ? r.erax_data.length > 0 : true);

  return (
    <div className="mt-1 p-2 bg-gray-50 rounded text-xs"><>
      <div className="flex items-center gap-2"><>
        {r.data_found ? <CheckCircle size={12} className="text-green-500" /> : <AlertCircle size={12} className="text-gray-400" />}
        <span className="font-medium text-gray-800">{r.entity_type}: {r.entity_value}</span>
        {hasEraxData && (
          <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded border border-green-200">ERAX Data</span>
        )}
      </></div>
      {r.tables_queried?.length > 0 && (
        <div className="mt-1 text-[10px] text-gray-500">Tables: {r.tables_queried.join(', ')}</div>
      )}
      {r.message && <p className="mt-1 text-gray-500 italic">{r.message}</p>}

      {/* ERAX SQL Query (collapsible) */}
      {r.erax_sql_query && (
        <div className="mt-2"><>
          <button
            onClick={() => setSqlExpanded(!sqlExpanded)}
            aria-expanded={sqlExpanded}
            aria-label={`${sqlExpanded ? 'Hide' : 'Show'} SQL query`}
            className="flex items-center gap-1 text-[10px] font-semibold text-blue-600 hover:text-blue-800"
          >
            {sqlExpanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
            SQL Query
          </button>
          {sqlExpanded && (
            <pre className="mt-1 p-2 bg-gray-900 text-green-300 rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
              {r.erax_sql_query}
            </pre>
          )}
        </></div>
      )}

      {/* ERAX Data Table (collapsible) */}
      {hasEraxData && (
        <div className="mt-1"><>
          <button
            onClick={() => setDataExpanded(!dataExpanded)}
            aria-expanded={dataExpanded}
            aria-label={`${dataExpanded ? 'Hide' : 'Show'} query results`}
            className="flex items-center gap-1 text-[10px] font-semibold text-purple-600 hover:text-purple-800"
          >
            {dataExpanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
            Query Results {Array.isArray(r.erax_data) && (() => {
              const first = r.erax_data[0] as Record<string, unknown> | undefined;
              if (first && 'rows' in first) {
                // Per-table format
                const total = (r.erax_data as { row_count: number }[]).reduce((s, t) => s + t.row_count, 0);
                return `(${total} rows across ${r.erax_data.length} table${r.erax_data.length !== 1 ? 's' : ''})`;
              }
              return `(${r.erax_data.length} rows)`;
            })()}
          </button>
          {dataExpanded && <EraxDataTable data={r.erax_data} />}
        </></div>
      )}

      {/* ERAX Narrative Response */}
      {r.erax_response && !r.erax_response.startsWith('ERAX query failed') && (
        <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200"><>
          <span className="text-[10px] font-semibold text-blue-600">ERAX Summary</span>
          <div className="mt-1">
            <MarkdownRenderer content={r.erax_response} />
          </div>
        </></div>
      )}
    </></div>
  );
}

/** Editable entity pill — click value to edit inline, × to remove. */
function EntityPill({
  entity, onRemove, onEditValue,
}: {
  entity: ExtractedEntity;
  onRemove: () => void;
  onEditValue: (newValue: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entity.value);

  // Sync draft when the entity value changes externally (e.g. parent re-render)
  useEffect(() => { setDraft(entity.value); }, [entity.value]);

  const commitEdit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== entity.value) {
      onEditValue(trimmed);
    } else {
      setDraft(entity.value);
    }
    setEditing(false);
  };

  const badgeColor = entity.confidence === 'high'
    ? 'bg-green-50 border-green-300 text-green-800'
    : entity.confidence === 'medium'
      ? 'bg-yellow-50 border-yellow-300 text-yellow-800'
      : 'bg-red-50 border-red-300 text-red-800';

  const bindings = entity.bindings ?? [];
  const bindingsTitle = bindings.length > 0
    ? `Bound to:\n${bindings.map(b => `  ${b.table_fqn}.${b.column}`).join('\n')}`
    : 'No explicit bindings — retrieval will guess columns from entity type';

  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${badgeColor}`}>
      <span className="font-medium">{entity.entity_type}:</span>
      {editing ? (
        <input
          type="text"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') { setDraft(entity.value); setEditing(false); } }}
          autoFocus
          className="font-mono font-bold bg-white border border-blue-400 rounded px-1 py-0 text-[10px] w-36 focus:outline-none"
        />
      ) : (
        <button
          onClick={() => setEditing(true)}
          title="Click to edit value"
          className="font-mono font-bold underline decoration-dashed cursor-text hover:bg-white/50 rounded px-0.5"
        >
          {entity.value}
        </button>
      )}
      {bindings.length > 0 && (
        <span
          className="ml-0.5 px-1 py-0 text-[8px] font-medium bg-white/60 border border-current/20 rounded"
          title={bindingsTitle}
        >
          → {bindings.length} {bindings.length === 1 ? 'column' : 'columns'}
        </span>
      )}
      <button
        onClick={onRemove}
        title="Remove entity"
        className="ml-0.5 text-red-400 hover:text-red-700 font-bold"
      >
        &times;
      </button>
    </span>
  );
}

/** A single row in the table-selection list. Owns its own expansion state
 *  and lazy-loads field descriptions the first time the user expands it.
 *  Each field row in the expanded panel lets the analyst bind a value to
 *  that column — the primary way to tell the system "query this value
 *  against this specific column" without relying on heuristics. */
function TableRow({
  table, rec, selected, onToggle, onBindField, entities, ticketKey,
}: {
  table: ModuleTable;
  rec: TableRecommendation | undefined;
  selected: boolean;
  onToggle: () => void;
  onBindField: (column: string, value: string) => void;
  entities: ExtractedEntity[];
  ticketKey: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [fields, setFields] = useState<TableField[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Per-field value drafts keyed by column name. Let the user type a value
  // next to a specific column and hit Enter or click the button to bind.
  const [bindDrafts, setBindDrafts] = useState<Record<string, string>>({});
  // Tracks recently-bound columns so the row can show a brief confirmation.
  // Key = column name, value = what was just bound. Cleared after 2.5 s.
  const [boundConfirm, setBoundConfirm] = useState<Record<string, string>>({});

  // ── Run Query (smoke-test) state ────────────────────────────────────────
  // Bindings on entities that target THIS table, collected once per render.
  const tableBindings = entities.flatMap((e) =>
    (e.bindings ?? [])
      .filter((b) => b.table_fqn === table.fqn)
      .map((b) => ({ value: e.value, column: b.column })),
  );
  const canRunQuery = tableBindings.length > 0;
  const [runQueryOpen, setRunQueryOpen] = useState(false);
  const [runQueryLoading, setRunQueryLoading] = useState(false);
  const [runQueryResult, setRunQueryResult] = useState<{
    sql: string;
    row_count: number;
    rows: Record<string, unknown>[];
    error: string | null;
  } | null>(null);

  const handleRunQuery = useCallback(async () => {
    setRunQueryOpen(true);
    setRunQueryLoading(true);
    setRunQueryResult(null);
    try {
      const res = await issueAnalysisApi.runTableQuery({
        ticket_key: ticketKey,
        table_fqn: table.fqn,
        bindings: tableBindings,
      });
      setRunQueryResult(res);
    } catch (e) {
      setRunQueryResult({
        sql: '',
        row_count: 0,
        rows: [],
        error: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setRunQueryLoading(false);
    }
  }, [ticketKey, table.fqn, tableBindings]);

  const tier = rec?.tier;
  const entityMatches = table.entity_matches ?? [];

  const handleToggleExpand = useCallback(() => {
    setExpanded(prev => {
      const next = !prev;
      if (next && fields === null && !loading) {
        setLoading(true);
        setError(null);
        issueAnalysisApi.getTableFields(table.fqn)
          .then(data => setFields(data.fields))
          .catch(err => setError(err instanceof Error ? err.message : 'Failed to load fields'))
          .finally(() => setLoading(false));
      }
      return next;
    });
  }, [table.fqn, fields, loading]);

  const submitBind = useCallback((column: string) => {
    const val = (bindDrafts[column] ?? '').trim();
    if (!val) return;
    onBindField(column, val);
    setBindDrafts(prev => ({ ...prev, [column]: '' }));
    // Show confirmation badge for 2.5 s then clear it.
    setBoundConfirm(prev => ({ ...prev, [column]: val }));
    setTimeout(() => {
      setBoundConfirm(prev => {
        const next = { ...prev };
        delete next[column];
        return next;
      });
    }, 2500);
  }, [bindDrafts, onBindField]);

  return (
    <div className={tier === 'rarely_used' || tier === 'hidden' ? 'opacity-60' : ''}>
      <div className="flex items-start gap-1.5 hover:bg-white rounded px-1 py-0.5">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select table ${table.name}`}
          className="mt-0.5 rounded border-gray-300 cursor-pointer"
        />
        <button
          type="button"
          onClick={handleToggleExpand}
          aria-expanded={expanded}
          aria-label={expanded ? `Hide fields for ${table.name}` : `Show fields for ${table.name}`}
          className="mt-0.5 flex-shrink-0 text-gray-400 hover:text-gray-700 transition-colors"
        >
          {expanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
        </button>
        <span className="text-[10px] leading-tight flex-1 min-w-0">
          <span className="font-medium text-gray-800">{table.name}</span>
          <span className="text-gray-400 ml-1">({table.module})</span>
          {entityMatches.map((em) => (
            <span
              key={`${table.fqn}-em-${em.entity_type}`}
              className="ml-1 px-1 py-0 text-[8px] bg-purple-100 text-purple-700 rounded border border-purple-200"
              title={`Column "${em.matched_field}" matches ${em.entity_type}`}
            >
              {em.entity_type}: {em.matched_field}
            </span>
          ))}
          {tier === 'auto_select' && (
            <span className="ml-1 px-1 py-0 text-[8px] bg-green-100 text-green-700 rounded">Auto</span>
          )}
          {tier === 'recommended' && (
            <span className="ml-1 px-1 py-0 text-[8px] bg-blue-100 text-blue-700 rounded">Recommended</span>
          )}
          {tier === 'rarely_used' && (
            <span className="ml-1 px-1 py-0 text-[8px] bg-gray-100 text-gray-500 rounded">Rare</span>
          )}
          {rec && rec.sample_count > 0 && (
            <span className="ml-1 text-[8px] text-gray-400">
              {Math.round(rec.selection_rate * 100)}%
            </span>
          )}
          {table.description && <span className="text-gray-400 ml-1">— {table.description}</span>}
        </span>
        {canRunQuery && (
          <button
            type="button"
            onClick={handleRunQuery}
            disabled={runQueryLoading}
            className="ml-1 flex-shrink-0 px-1.5 py-0 text-[9px] bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50"
            title={`Run SELECT TOP 50 * FROM ${table.fqn} WHERE … (${tableBindings.length} binding${tableBindings.length === 1 ? '' : 's'})`}
          >
            {runQueryLoading ? 'Running…' : 'Run Query'}
          </button>
        )}
      </div>

      {expanded && (
        <div className="ml-7 mt-1 mb-1 p-2 bg-white border border-gray-200 rounded">
          {loading && (
            <p className="text-[10px] text-gray-400 italic">Loading fields…</p>
          )}
          {error && (
            <p className="text-[10px] text-red-600">Failed to load fields: {error}</p>
          )}
          {!loading && !error && fields && fields.length === 0 && (
            <p className="text-[10px] text-gray-400 italic">No fields found for this table.</p>
          )}
          {!loading && !error && fields && fields.length > 0 && (
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-0.5 pr-2 font-semibold">Field</th>
                  <th className="py-0.5 pr-2 font-semibold">Type</th>
                  <th className="py-0.5 pr-2 font-semibold">Description</th>
                  <th className="py-0.5 font-semibold w-44" title="Type a value and click Bind to pin this column to a specific entity. The entity will appear in the Entities section above with an explicit column mapping used during analysis.">
                    Bind value
                    <span className="ml-1 text-gray-400 font-normal">ⓘ</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {fields.map((f) => (
                  <tr key={`${table.fqn}-${f.name}`} className="border-b border-gray-100 last:border-b-0">
                    <td className="py-0.5 pr-2 font-mono text-gray-800 align-top whitespace-nowrap">{f.name}</td>
                    <td className="py-0.5 pr-2 text-gray-600 align-top whitespace-nowrap">{f.data_type || '—'}</td>
                    <td className="py-0.5 pr-2 text-gray-600 align-top">{f.description || <span className="italic text-gray-400">No description</span>}</td>
                    <td className="py-0.5 align-top">
                      <div className="flex items-center gap-1 min-w-[160px]">
                        {boundConfirm[f.name] ? (
                          // Confirmation state — shown for 2.5 s after a successful bind.
                          <span className="flex items-center gap-1 text-[10px] text-green-700 font-medium animate-pulse">
                            <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 12 12" fill="none">
                              <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Bound: <span className="font-mono">{boundConfirm[f.name]}</span>
                            <span className="text-green-500">→ entity updated</span>
                          </span>
                        ) : (
                          <>
                            <input
                              type="text"
                              value={bindDrafts[f.name] ?? ''}
                              onChange={e => setBindDrafts(prev => ({ ...prev, [f.name]: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitBind(f.name); } }}
                              placeholder="value..."
                              aria-label={`Bind value to column ${f.name}`}
                              className="text-[10px] px-1 py-0 border border-gray-300 rounded w-24 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                            <button
                              type="button"
                              onClick={() => submitBind(f.name)}
                              disabled={!(bindDrafts[f.name] ?? '').trim()}
                              className="text-[9px] px-1.5 py-0 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                              Bind
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Run-Query results modal — opens when the analyst clicks the
          per-table "Run Query" button. Shows the SQL, row count, and
          up to 50 result rows. */}
      {runQueryOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setRunQueryOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="font-semibold text-gray-800">Smoke-test query</h3>
                <p className="text-xs text-gray-500 mt-0.5">{table.fqn}</p>
              </div>
              <button
                type="button"
                onClick={() => setRunQueryOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-700 rounded"
                aria-label="Close results"
              >
                <span className="text-lg">×</span>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {runQueryLoading && (
                <p className="text-sm text-gray-500">Running query…</p>
              )}
              {!runQueryLoading && runQueryResult && (
                <>
                  <div>
                    <div className="text-[10px] uppercase font-semibold text-gray-500 mb-1">SQL</div>
                    <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 whitespace-pre-wrap break-all font-mono">
                      {runQueryResult.sql || '(no SQL was generated)'}
                    </pre>
                  </div>
                  {runQueryResult.error ? (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                      {runQueryResult.error}
                    </div>
                  ) : (
                    <div>
                      <div className="text-sm text-gray-700 mb-2">
                        <span className="font-semibold">{runQueryResult.row_count}</span>
                        {' '}
                        row{runQueryResult.row_count === 1 ? '' : 's'} returned
                        {runQueryResult.row_count >= 50 && (
                          <span className="text-xs text-gray-500 ml-2">(capped at 50)</span>
                        )}
                      </div>
                      {runQueryResult.rows.length > 0 ? (
                        <div className="border border-gray-200 rounded overflow-x-auto">
                          <SimpleTable rows={runQueryResult.rows} maxRows={50} />
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          No rows. The bindings did not match any data in this table.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Table selection with entity-aware auto-detection.
 *  Shows why each table was selected (learned pattern, entity field match, or module default). */
function TableAutoSelection({
  availableTables, selectedTables, setSelectedTables, tableRecs,
  showHidden, setShowHidden, tablesLoading, tablesError, confirmedModules, fetchTables,
  onBindField, entities, ticketKey,
}: {
  availableTables: ModuleTable[];
  selectedTables: Set<string>;
  setSelectedTables: React.Dispatch<React.SetStateAction<Set<string>>>;
  tableRecs: Map<string, TableRecommendation>;
  showHidden: boolean;
  setShowHidden: (v: boolean) => void;
  tablesLoading: boolean;
  tablesError: string | null;
  confirmedModules: string[];
  fetchTables: (modules: string[]) => void;
  onBindField: (tableFqn: string, column: string, value: string) => void;
  /** Current entities (with their bindings) — used by TableRow to compute
   *  the (value, column) pairs to send when the analyst clicks Run Query
   *  on a specific table. */
  entities: ExtractedEntity[];
  /** Ticket key — passed to /issue-analysis/run-table-query so the
   *  backend can derive the LRP client. */
  ticketKey: string;
}) {
  if (tablesLoading) {
    return <p className="text-[10px] text-gray-400">Loading tables...</p>;
  }
  if (tablesError) {
    return (
      <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
        <span>Failed to load tables: {tablesError}</span>
        <button
          onClick={() => fetchTables(confirmedModules)}
          className="px-2 py-0.5 bg-red-600 text-white rounded text-[10px] hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }
  if (availableTables.length === 0 && confirmedModules.length > 0) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-gray-500">No tables loaded.</span>
        <button
          onClick={() => fetchTables(confirmedModules)}
          className="px-2 py-0.5 bg-blue-600 text-white rounded text-[10px] hover:bg-blue-700 transition-colors"
        >
          Load Tables
        </button>
      </div>
    );
  }
  if (availableTables.length === 0) return null;

  // Compute selection reason counts for the summary bar
  const autoCount = availableTables.filter(t => tableRecs.get(t.fqn)?.tier === 'auto_select').length;
  const recCount = availableTables.filter(t => tableRecs.get(t.fqn)?.tier === 'recommended').length;
  const entityMatchCount = availableTables.filter(t => (t.entity_matches?.length ?? 0) > 0).length;
  const hasLearned = availableTables.some(t => (tableRecs.get(t.fqn)?.sample_count ?? 0) > 0);

  const visibleTables = availableTables.filter(t => {
    if (t.excluded) return false; // tmp*, *_arch, *_log, *_bak etc. — never shown
    const rec = tableRecs.get(t.fqn);
    if (rec?.tier === 'hidden' && !showHidden) return false;
    return true;
  });
  const hiddenCount = availableTables.filter(t => !t.excluded && tableRecs.get(t.fqn)?.tier === 'hidden' && !showHidden).length;
  // Sort: entity-matched first, then auto_select, recommended, normal, rarely_used,
  // then alphabetical (case-insensitive so lowercase-starting tables like activityorder
  // appear in correct A-Z position rather than after all uppercase names).
  const tierOrder = { auto_select: 0, recommended: 1, normal: 2, rarely_used: 3, hidden: 4 };
  const sorted = [...visibleTables].sort((a, b) => {
    // Entity-matched tables (high-probability) float to top
    const aMatch = (a.entity_matches?.length ?? 0) > 0 ? -1 : 0;
    const bMatch = (b.entity_matches?.length ?? 0) > 0 ? -1 : 0;
    if (aMatch !== bMatch) return aMatch - bMatch;
    const ta = tableRecs.get(a.fqn)?.tier || 'normal';
    const tb = tableRecs.get(b.fqn)?.tier || 'normal';
    const tierDiff = (tierOrder[ta] ?? 2) - (tierOrder[tb] ?? 2);
    if (tierDiff !== 0) return tierDiff;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });

  // Build summary reason parts
  const reasonParts: string[] = [];
  if (entityMatchCount > 0) reasonParts.push(`${entityMatchCount} matched by entity fields`);
  if (autoCount > 0) reasonParts.push(`${autoCount} auto-selected from history`);
  if (recCount > 0) reasonParts.push(`${recCount} recommended`);
  if (reasonParts.length === 0 && !hasLearned) reasonParts.push('all module tables included (no usage history yet)');

  return (
    <div className="space-y-1.5">
      {/* Selection summary bar */}
      <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded text-xs">
        <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
        <div className="flex-1">
          <span className="font-medium text-green-800">
            {selectedTables.size} of {availableTables.filter(t => !t.excluded).length} tables pre-selected
          </span>
          <span className="text-green-600 ml-1">
            — {reasonParts.join(', ')}
          </span>
        </div>
      </div>

      {selectedTables.size === 0 && (
        <p className="text-xs text-amber-600">No tables selected — the system will query the top tables from Neo4j automatically.</p>
      )}

      {/* Full table list — always visible */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
            Tables ({selectedTables.size}/{availableTables.filter(t => !t.excluded).length} selected)
          </span>
          <button
            onClick={() => setSelectedTables(new Set(visibleTables.map(t => t.fqn)))}
            className="text-[9px] text-blue-600 hover:underline"
          >
            Select All
          </button>
          <button
            onClick={() => setSelectedTables(new Set())}
            className="text-[9px] text-blue-600 hover:underline"
          >
            Deselect All
          </button>
        </div>
        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded p-1.5 bg-gray-50 space-y-0.5">
          {sorted.map((t) => (
            <TableRow
              key={t.fqn}
              table={t}
              rec={tableRecs.get(t.fqn)}
              selected={selectedTables.has(t.fqn)}
              onToggle={() => {
                setSelectedTables(prev => {
                  const next = new Set(prev);
                  if (next.has(t.fqn)) next.delete(t.fqn); else next.add(t.fqn);
                  return next;
                });
              }}
              onBindField={(column, value) => onBindField(t.fqn, column, value)}
              entities={entities}
              ticketKey={ticketKey}
            />
          ))}
        </div>
        {hiddenCount > 0 && (
          <button
            onClick={() => setShowHidden(!showHidden)}
            className="text-[9px] text-gray-500 hover:text-gray-700 mt-1"
          >
            {showHidden ? 'Hide' : 'Show'} {hiddenCount} rarely used table{hiddenCount > 1 ? 's' : ''}
          </button>
        )}
      </div>
    </div>
  );
}

/** Shared JIRA ticket content — used by both the pre-confirmation preview and
 *  the post-analysis JIRA tab to avoid duplication. */
function TicketDetailsContent({ ticket, keyPrefix, showModulesAndEntities, userCommentBox, onModuleClick, entitiesOverride, entityActions, onOpenEntityTools }: {
  ticket: StructuredTicket;
  keyPrefix: string;
  showModulesAndEntities?: boolean;
  /** Opens the floating Entity Types / module setup window. Omitted on the
   *  intake preview, where the maintenance editors are not in play. */
  onOpenEntityTools?: () => void;
  /** Live entity list + edit handlers — the JIRA tab passes these so the
   *  table reflects analyst corrections instead of the intake snapshot. */
  entitiesOverride?: ExtractedEntity[];
  entityActions?: EntityTableActions;
  /** Pre-rendered user-comment widget, slotted below the extracted screenshot
   *  text section. The parent owns the textarea state so the comment value
   *  survives across this component's intake-preview vs JIRA-tab incarnations. */
  userCommentBox?: React.ReactNode;
  onModuleClick?: (moduleName: string) => void;
}) {
  return (
    <div className="space-y-2.5">
      {/* Summary */}
      {ticket.summary && (
        <div>
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Summary</span>
          <div className="mt-1 text-sm text-gray-800 font-medium leading-snug">
            {ticket.summary}
          </div>
        </div>
      )}

      {/* Core metadata grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs">
        <div className="flex items-center gap-1 text-gray-500">
          <Layers size={10} /> Project:
          <span className="text-gray-800 font-medium">{ticket.project || 'N/A'}</span>
          {ticket.project && VPN_CLIENT_PREFIXES.some(
            (vpn) => ticket.project!.toUpperCase().startsWith(vpn)
          ) && (
            <span style={{
              display: 'inline-block', marginLeft: 4,
              padding: '1px 5px', borderRadius: 8, fontSize: 9, fontWeight: 600,
              background: '#fef3c7', color: '#92400e', whiteSpace: 'nowrap',
            }}>VPN client</span>
          )}
        </div>
        <div className="flex items-center gap-1 text-gray-500">
          <AlertTriangle size={10} /> Priority:
          <span className="text-gray-800 font-medium">{ticket.priority || 'N/A'}</span>
        </div>
        <div><span className="text-gray-500">Status:</span> <span className="text-gray-800 font-medium">{ticket.status}</span></div>
        <div className="flex items-center gap-1 text-gray-500">
          <User size={10} /> Assignee:
          <span className="text-gray-800 font-medium">{ticket.assignee || 'Unassigned'}</span>
        </div>
        <div className="flex items-center gap-1 text-gray-500">
          <User size={10} /> Reporter:
          <span className="text-gray-800 font-medium">{ticket.reporter || 'N/A'}</span>
        </div>
        <div><span className="text-gray-500">Type:</span> <span className="font-mono text-gray-800">{ticket.issue_type}</span></div>
        {ticket.created && (
          <div className="flex items-center gap-1 text-gray-500">
            <Calendar size={10} /> Created:
            <span className="text-gray-800 font-medium">{new Date(ticket.created).toLocaleDateString()}</span>
          </div>
        )}
        {ticket.updated && (
          <div className="flex items-center gap-1 text-gray-500">
            <Calendar size={10} /> Updated:
            <span className="text-gray-800 font-medium">{new Date(ticket.updated).toLocaleDateString()}</span>
          </div>
        )}
        {ticket.affected_client && (
          <div><span className="text-gray-500">Client:</span> <span className="text-gray-800">{ticket.affected_client}</span></div>
        )}
        {ticket.environment && (
          <div><span className="text-gray-500">Environment:</span> <span className="text-gray-800">{ticket.environment}</span></div>
        )}
        {ticket.resolution && (
          <div><span className="text-gray-500">Resolution:</span> <span className="text-gray-800">{ticket.resolution}</span></div>
        )}
        {ticket.product_version && (
          <div><span className="text-gray-500">Version:</span> <span className="text-gray-800">{ticket.product_version}</span></div>
        )}
      </div>

      {/* Components & Labels */}
      {ticket.components?.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap text-xs">
          <span className="text-[10px] text-gray-500 font-semibold">Components:</span>
          {ticket.components.map((c, i) => (
            <span key={`${keyPrefix}-comp-${i}`} className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded">{c}</span>
          ))}
        </div>
      )}
      {ticket.labels?.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap text-xs">
          <Tag size={10} className="text-gray-400" />
          {ticket.labels.map((l, i) => (
            <span key={`${keyPrefix}-lbl-${i}`} className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">{l}</span>
          ))}
        </div>
      )}

      {/* Condensed Summary */}
      {ticket.condensed_summary && (
        <div>
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">AI Summary</span>
          <div className="mt-1 text-xs text-gray-700 leading-relaxed bg-blue-50 border border-blue-200 rounded p-2">
            {ticket.condensed_summary}
          </div>
        </div>
      )}

      {/* Description */}
      {ticket.description && (
        <div>
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Description</span>
          <div className="mt-1 text-xs text-gray-600 leading-relaxed bg-gray-50 rounded p-2 max-h-48 overflow-y-auto">
            <MarkdownRenderer content={ticket.description} />
          </div>
        </div>
      )}

      {/* Steps to Reproduce */}
      {ticket.steps_to_reproduce && (
        <div>
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Steps to Reproduce</span>
          <div className="mt-1 text-xs text-gray-600 leading-relaxed bg-gray-50 rounded p-2 max-h-32 overflow-y-auto">
            <MarkdownRenderer content={ticket.steps_to_reproduce} />
          </div>
        </div>
      )}

      {/* Modules & Entities (only in the post-analysis JiraTab) */}
      {showModulesAndEntities && (
        <>
          {ticket.modules?.length > 0 && (
            <div>
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Detected Modules</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {ticket.modules.map((m) => (
                  <button
                    key={`${keyPrefix}-mod-${m}`}
                    onClick={() => onModuleClick?.(m)}
                    className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-200 hover:bg-blue-100 hover:border-blue-300 transition-colors cursor-pointer"
                    title="Click to view module graph"
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Extracted Entities</span>
              {onOpenEntityTools && (
                <button
                  onClick={onOpenEntityTools}
                  className="flex items-center gap-1 px-2 py-0.5 rounded border border-primary-300 bg-primary-50 text-primary-700 text-[10px] font-semibold hover:bg-primary-100 transition-colors"
                  title="Add a missing entity type, module grouping, master lookup or stoplist entry — without leaving this page"
                >
                  <Settings2 size={11} />
                  Entity Types
                </button>
              )}
            </div>
            <EntityTable entities={entitiesOverride ?? ticket.entities} actions={entityActions} />
          </div>
        </>
      )}

      {/* Comments */}
      {ticket.comments && ticket.comments.length > 0 && (
        <div>
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
            Comments ({ticket.comments.length})
          </span>
          <div className="mt-1 space-y-1.5 max-h-60 overflow-y-auto">
            {ticket.comments.map((comment, i) => (
              <div key={`${keyPrefix}-comment-${i}`} className="text-xs p-2 bg-gray-50 rounded border-l-2 border-blue-200">
                <div className="flex items-center gap-2 text-[10px] text-gray-400 mb-1">
                  <User size={10} />
                  <span className="font-medium text-gray-600">{comment.author}</span>
                  {comment.created && <span>{new Date(comment.created).toLocaleString()}</span>}
                </div>
                <div className="text-gray-700 whitespace-pre-wrap">{comment.body}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Linked Issues */}
      {ticket.linked_issues && ticket.linked_issues.length > 0 && (
        <div>
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
            Linked Issues ({ticket.linked_issues.length})
          </span>
          <div className="mt-1 space-y-1">
            {ticket.linked_issues.map((link) => (
              <div key={`${keyPrefix}-link-${link.key}`} className="flex items-center gap-2 text-xs p-1.5 bg-gray-50 rounded">
                <span className="text-gray-500 italic">{link.relationship}</span>
                <span className="font-medium text-blue-700">{link.key}</span>
                <span className="text-gray-600 flex-1 truncate">{link.summary}</span>
                <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">{link.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attachments + per-image OCR status */}
      {ticket.attachments?.length > 0 && (
        <div>
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
            Attachments ({ticket.attachments.length})
          </span>
          <div className="mt-1 space-y-1">
            {ticket.attachments.map((a, i) => {
              const imgStatus = ticket.image_extraction_results?.find(r => r.filename === a);
              const isDoc = /\.(xlsx|csv|txt|pdf|docx)$/i.test(a);
              return (
                <div key={`${keyPrefix}-att-${i}`} className="space-y-0.5">
                  <div className="flex items-center gap-1.5 text-xs text-gray-600">
                    <Paperclip size={10} className="text-gray-400 flex-shrink-0" />
                    <span className="truncate">{a}</span>
                    {imgStatus && imgStatus.status === 'success' && (
                      <span className="flex items-center gap-0.5 text-[10px] text-green-600" title={isDoc ? 'Document content read into the analysis' : 'Text extracted via OCR'}>
                        <CheckCircle size={10} /> {isDoc ? 'Read' : 'OCR'}
                      </span>
                    )}
                    {imgStatus && imgStatus.status === 'failed' && (
                      <span className="flex items-center gap-0.5 text-[10px] text-red-500" title={imgStatus.detail || 'Extraction failed'}>
                        <AlertCircle size={10} /> Failed
                      </span>
                    )}
                    {imgStatus && imgStatus.status === 'skipped' && (
                      <span className="flex items-center gap-0.5 text-[10px] text-gray-400" title={imgStatus.detail || 'Skipped'}>
                        <AlertCircle size={10} /> Skipped
                      </span>
                    )}
                  </div>
                  {imgStatus?.status === 'failed' && imgStatus.detail && (
                    <p className="ml-5 text-[10px] text-red-400 italic">{imgStatus.detail}</p>
                  )}
                  {imgStatus?.text_preview && (
                    <div className="ml-5 p-1.5 bg-green-50 rounded border border-green-200 text-[10px] text-gray-600 font-mono whitespace-pre-wrap max-h-20 overflow-y-auto">
                      {imgStatus.text_preview}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Full extracted image text (expandable) */}
      {ticket.extracted_image_text && (
        <div>
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
            Full Extracted Screenshot Text
          </span>
          <p className="mt-0.5 text-[9px] text-gray-400 italic">
            AI-extracted text — may contain errors. Verify against the original images.
          </p>
          <div className="mt-1 p-2 bg-blue-50 rounded border border-blue-200 text-xs text-gray-700 max-h-48 overflow-y-auto whitespace-pre-wrap font-mono">
            {ticket.extracted_image_text}
          </div>
        </div>
      )}

      {/* Full extracted document text (pdf/docx/xlsx/csv/msg) — parity with
          the screenshot text above; previously invisible past a 200-char
          preview (1.68.8) */}
      {ticket.extracted_document_text && (
        <div>
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
            Full Extracted Document Text
          </span>
          <p className="mt-0.5 text-[9px] text-gray-400 italic">
            Read from pdf/docx/xlsx/csv/msg attachments — exactly what the analysis sees.
          </p>
          <div className="mt-1 p-2 bg-green-50 rounded border border-green-200 text-xs text-gray-700 max-h-48 overflow-y-auto whitespace-pre-wrap font-mono">
            {ticket.extracted_document_text}
          </div>
        </div>
      )}

      {/* Pre-analysis user comment slot. Rendered by the parent so the
          textarea state survives across the intake-preview vs JIRA-tab
          incarnations of this component. */}
      {userCommentBox}

      {/* Image warning */}
      {!ticket.images_received && ticket.image_warning && (
        <div className="flex items-start gap-2 p-2 bg-amber-50 rounded border border-amber-200 text-xs text-amber-800">
          <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <span>{ticket.image_warning}</span>
        </div>
      )}
    </div>
  );
}

/** Collapsible JIRA details preview — shown in the confirmation step so the analyst
 *  can read the full ticket context before confirming entities & modules. */
function JiraDetailsPreview({ ticket, userCommentBox, onModuleClick }: { ticket: StructuredTicket; userCommentBox?: React.ReactNode; onModuleClick?: (moduleName: string) => void }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={`${open ? 'Collapse' : 'Expand'} JIRA ticket details`}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors bg-gray-50/50"
      >
        {open ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
        <FileText size={14} className="text-blue-500" />
        <span className="text-xs font-semibold text-gray-800 flex-1">JIRA Ticket Details</span>
        <span className="text-[10px] text-gray-400">
          {open ? 'Click to collapse' : 'Click to expand — review before confirming'}
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-2 border-t border-gray-100">
          <HintCaptureMenu
            ticketKey={ticket.ticket_key}
            availableModules={ticket.modules ?? []}
          >
            <TicketDetailsContent ticket={ticket} keyPrefix="preview" userCommentBox={userCommentBox} onModuleClick={onModuleClick} />
          </HintCaptureMenu>
        </div>
      )}
    </div>
  );
}

/** JIRA ticket key format: PROJECT-123 */
const TICKET_KEY_RE = /^[A-Z][A-Z0-9]+-\d+$/;

/** Projects whose infra sits behind a VPN — shown with a badge in the JIRA tab */
const VPN_CLIENT_PREFIXES = ['TASLI']; // ESL provisioned 2026-07-14, LSC (SAIL) 2026-07-15, HSL 2026-07-24, SWIRE 2026-10-07

/** ~3 months in ms (90 days) — threshold for the event-timeline age warning */
const TIMELINE_AGE_LIMIT_MS = 90 * 24 * 60 * 60 * 1000;

/** Warning shown when the JIRA ticket is older than TIMELINE_AGE_LIMIT_MS */
const TIMELINE_AGE_WARNING = 'Event timeline will not work as Elapsed time is >3 Months';

interface IssueAnalysisPanelProps {
  /** Pre-fill ticket key from external selection (e.g. history sidebar). */
  initialTicketKey?: string;
  /** Load a specific analysis by DB row ID (from history sidebar). */
  initialAnalysisId?: number;
  /** Called after a successful analysis completes. */
  onAnalysisComplete?: (ticketKey: string) => void;
  /** If `initialTicketKey` has no saved analysis (404), automatically run
   *  intake (Phase 1 only — cheap, stops at the confirm screen) instead of
   *  showing "Failed to load analysis". Used by Chat's ticket-key redirect
   *  link, where the analyst clicked with intent to work the ticket, not
   *  just browse history. Off by default — the ordinary history-sidebar
   *  and "return to ticket" paths must keep showing a real error on 404. */
  autoIntakeIfMissing?: boolean;
}

/** Main Issue Analysis Panel. */
export function IssueAnalysisPanel({ initialTicketKey, initialAnalysisId, onAnalysisComplete, autoIntakeIfMissing }: IssueAnalysisPanelProps) {
  const [ticketKey, setTicketKey] = useState(initialTicketKey || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IssueAnalysisResponse | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Flowchart state
  const [showFlowchartModal, setShowFlowchartModal] = useState(false);
  const [selectedModuleForGraph, setSelectedModuleForGraph] = useState<string | null>(null);
  const [currentStepId, setCurrentStepId] = useState<number | null>(null);

  // Two-step flow: intake → confirm modules + entities → full analysis
  const [intakeResult, setIntakeResult] = useState<StructuredTicket | null>(null);
  // SQL Investigation cards captured on the pre-analysis confirmation
  // screen. Carry across into the post-analysis view via `seedPreCards`
  // below so the analyst's pre-analysis work isn't lost.
  const [preCards, setPreCards] = useState<SqlCard[]>([]);
  // Auto-seed status for the pre-analysis SQL Investigation pane. The
  // seeder fires from a useEffect below whenever the (modules, entities,
  // selected tables) tuple stabilises, feeding everything to the LLM and
  // populating preCards with up to 10 compound SELECTs.
  const [seedingPreCards, setSeedingPreCards] = useState(false);
  const [preCardsError, setPreCardsError] = useState<string | null>(null);
  const [preCardsSkipped, setPreCardsSkipped] = useState<{ fqn: string; reason: string }[]>([]);
  const [preCardsSemantic, setPreCardsSemantic] = useState<
    { table_fqn: string; matched_field: string; score: number; already_selected: boolean; auto_included: boolean }[]
  >([]);
  const [preCardsStructural, setPreCardsStructural] = useState<
    { table_fqn: string; matched_term: string; score: number; source_hits: number; already_selected: boolean; auto_included: boolean }[]
  >([]);
  // Tracks the last (modules, entities, tables) signature we sent to the
  // seeder so we don't re-fire on every render — only when the inputs
  // materially change. Carries the ticket key too so swapping tickets
  // always re-seeds.
  const lastSeedKeyRef = useRef<string | null>(null);
  const seedAbortRef = useRef<AbortController | null>(null);
  const [confirmedModules, setConfirmedModules] = useState<string[]>([]);
  const [moduleInput, setModuleInput] = useState('');
  // Full list of module names from Neo4j (lazy-loaded once for the typeahead).
  const [allModules, setAllModules] = useState<string[]>([]);
  // Whether the suggestion dropdown is visible.
  const [showModuleSuggestions, setShowModuleSuggestions] = useState(false);
  // Highlighted suggestion index for keyboard navigation.
  const [moduleSuggestionIndex, setModuleSuggestionIndex] = useState(0);
  const [confirmedEntities, setConfirmedEntities] = useState<ExtractedEntity[]>([]);
  const confirmedEntitiesRef = useRef(confirmedEntities);
  confirmedEntitiesRef.current = confirmedEntities;
  // Label + value inputs for the "add identifier without bindings" fallback
  // form. The primary way to add entities is now the Bind field in the
  // Fields panel, which sets explicit (table, column) bindings.
  const [entityTypeInput, setEntityTypeInput] = useState('');
  const [entityValueInput, setEntityValueInput] = useState('');
  const [availableTables, setAvailableTables] = useState<ModuleTable[]>([]);
  const [selectedTables, setSelectedTables] = useState<Set<string>>(new Set());
  const [tablesLoading, setTablesLoading] = useState(false);
  const [tableRecs, setTableRecs] = useState<Map<string, TableRecommendation>>(new Map());
  const [showHidden, setShowHidden] = useState(false);
  const [tablesError, setTablesError] = useState<string | null>(null);

  // Pre-analysis free-text comment. Captured below the AI-extracted
  // screenshot text section so the analyst can add module/table context
  // that's missing from JIRA before clicking Analyze. The comment is sent
  // with the analyze call, fed into the Phase 1 / Phase 3 / Final Summary
  // LLM prompts on the backend, and persisted on the result row.
  // `savingUserComment` blocks duplicate clicks on the note-save button.
  const USER_COMMENT_MAX_LEN = 2000;
  const [userComment, setUserComment] = useState('');
  const [savingUserComment, setSavingUserComment] = useState(false);
  const [userCommentSavedAt, setUserCommentSavedAt] = useState<string | null>(null);
  const [userCommentSavedBy, setUserCommentSavedBy] = useState<string | null>(null);

  // Set once by the load effect below when getAnalysis 404s AND the caller
  // opted into auto-intake (autoIntakeIfMissing). A separate effect below
  // (after runIntake is declared) consumes this once — kept out of THIS
  // effect so the ordinary history-sidebar / "return to ticket" paths
  // (autoIntakeIfMissing unset) are byte-for-byte unchanged: same error
  // message, same behavior, on any 404.
  const [autoIntakeSignal, setAutoIntakeSignal] = useState<string | null>(null);

  // When external selection changes, load that ticket's cached analysis
  useEffect(() => {
    if (!initialTicketKey) return;
    const key = initialTicketKey.trim().toUpperCase();
    setTicketKey(key);
    // Load cached analysis for this ticket (by specific ID if provided)
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    setResult(null);
    issueAnalysisApi.getAnalysis(key, initialAnalysisId)
      .then(data => {
        if (!controller.signal.aborted) setResult(data);
      })
      .catch(err => {
        if (controller.signal.aborted) return;
        if (autoIntakeIfMissing && err instanceof ApiError && err.status === 404) {
          setAutoIntakeSignal(key);
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load analysis');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
  }, [initialTicketKey]);

  // Keep the user-comment textbox in sync with the loaded analysis: pre-fill
  // when a ticket has a stored comment, clear when the ticket has none.
  // Runs whenever result swaps (cached load, fresh analyze, or ticket change).
  useEffect(() => {
    if (result) {
      setUserComment(result.user_input_comment ?? '');
      setUserCommentSavedAt(result.user_input_comment_updated_at ?? null);
      setUserCommentSavedBy(result.user_input_comment_updated_by ?? null);
    } else {
      setUserComment('');
      setUserCommentSavedAt(null);
      setUserCommentSavedBy(null);
    }
  }, [result]);

  // ── Auto-seed the pre-analysis Saved Results pane ──
  //
  // The moment we have a confirmed (modules + entities + selectedTables)
  // tuple, ask the backend to feed the ticket summary + entities + table
  // column descriptions to the LLM and generate up to 10 compound SELECT
  // queries against LRP. The seeded cards land in `preCards` tagged AUTO.
  // Manual cards (source === 'manual') are preserved across re-seeds; any
  // prior AUTO cards are replaced.
  //
  // Debounced 1.2s so editing modules/entities doesn't fire a flurry of
  // LLM calls. We track a content-based seedKey so we only re-fire when
  // inputs materially change (not on every render).
  useEffect(() => {
    if (!intakeResult) return;
    if (confirmedModules.length === 0) return;
    if (confirmedEntities.length === 0) return;
    if (selectedTables.size === 0) return;
    if (availableTables.length === 0) return;

    const seedKey = [
      intakeResult.ticket_key,
      [...confirmedModules].sort().join('|'),
      [...confirmedEntities]
        .map((e) => `${e.entity_type}=${e.value}`)
        .sort()
        .join('|'),
      Array.from(selectedTables).sort().join('|'),
    ].join('::');
    if (seedKey === lastSeedKeyRef.current) return;

    const timer = window.setTimeout(() => {
      // Commit the dedupe key only now, when the request actually fires.
      // Setting it at schedule time let a same-content re-render inside the
      // debounce window cancel the timer AND pass the dedupe check on the
      // re-run — the request was never sent and never rescheduled, leaving
      // Saved Results at "Waiting…" forever.
      lastSeedKeyRef.current = seedKey;
      seedAbortRef.current?.abort();
      const controller = new AbortController();
      seedAbortRef.current = controller;
      setSeedingPreCards(true);
      setPreCardsError(null);

      issueAnalysisApi
        .seedPreCards(
          {
            ticket_key: intakeResult.ticket_key,
            ticket: intakeResult,
            modules: confirmedModules,
            entities: confirmedEntities,
            tables: Array.from(selectedTables),
          },
          controller.signal,
        )
        .then((resp) => {
          if (controller.signal.aborted) return;
          const seeded: SqlCard[] = resp.cards.map((c) => ({
            id: `auto-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            label: c.label || 'untitled',
            sql: c.sql,
            rows: c.rows as Record<string, unknown>[],
            rowCount: c.row_count,
            kind: c.kind === 'timeline' ? 'timeline' : 'data',
            source: 'auto',
            edited: false,
            rationale: c.rationale || undefined,
            error: c.error ?? undefined,
          }));
          // Replace prior AUTO cards, keep manual ones intact.
          setPreCards((prev) => [...prev.filter((c) => c.source === 'manual'), ...seeded]);
          setPreCardsSkipped(resp.skipped_tables ?? []);
          setPreCardsSemantic(resp.semantic_suggestions ?? []);
          setPreCardsStructural(resp.structural_suggestions ?? []);
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          setPreCardsError(err instanceof Error ? err.message : String(err));
        })
        .finally(() => {
          if (!controller.signal.aborted) setSeedingPreCards(false);
        });
    }, 1200);

    return () => {
      window.clearTimeout(timer);
    };
  }, [intakeResult, confirmedModules, confirmedEntities, selectedTables, availableTables.length]);

  // ── Restore persisted auto-seeded cards ──
  //
  // Seeded cards are persisted server-side when seed-pre-cards completes.
  // When a saved analysis is reopened there is no live intake session, so
  // the auto-seeder above can't re-fire — restore the last seed from the
  // backend instead. Manual cards are untouched; restored cards carry
  // source 'auto' exactly like a live seed, so a later re-seed replaces
  // them the same way.
  useEffect(() => {
    if (!ticketKey || !result) return;
    // While a live seed is running, wait — the restore must not race the
    // fresh seed's server-side persistence. (This effect mostly serves
    // reopened analyses; the live pre-analysis pane gets its cards from
    // the seeder response directly.)
    if (seedingPreCards) return;

    const controller = new AbortController();
    issueAnalysisApi
      .getPreCards(ticketKey, controller.signal)
      .then((resp) => {
        if (controller.signal.aborted || resp.cards.length === 0) return;
        const restored: SqlCard[] = resp.cards.map((c) => ({
          id: `auto-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          label: c.label || 'untitled',
          sql: c.sql,
          rows: c.rows as Record<string, unknown>[],
          rowCount: c.row_count,
          kind: c.kind === 'timeline' ? 'timeline' : 'data',
          source: 'auto',
          edited: false,
          rationale: c.rationale || undefined,
          error: c.error ?? undefined,
        }));
        // The server is the source of truth for PRISTINE auto cards —
        // adopt the persisted set, but never at the cost of analyst work:
        // manual cards AND analyst-edited auto cards survive (saving an
        // edit keeps source 'auto' with edited=true, and the old filter
        // silently destroyed those edits). Restored duplicates of a kept
        // card are dropped by normalized SQL. The old some(auto) guard is
        // gone because it froze a stale 4-card seed forever while 15
        // newer cards sat in the DB (observed twice on MILAHA-2266).
        setPreCards((prev) => {
          const kept = prev.filter((c) => c.source === 'manual' || c.edited);
          const normalize = (sql: string) => sql.split(/\s+/).join(' ').toLowerCase();
          const keptSql = new Set(kept.map((c) => normalize(c.sql)));
          return [...kept, ...restored.filter((c) => !keptSql.has(normalize(c.sql)))];
        });
        setPreCardsSkipped(resp.skipped_tables ?? []);
      })
      .catch(() => {
        /* restore is best-effort — the pane just stays empty */
      });
    return () => controller.abort();
  }, [ticketKey, result, seedingPreCards]);

  // Save the pre-analysis comment as a note (no re-analysis). Disabled when
  // there is no analysis_id yet (the user hasn't run Analyze), when the
  // current draft matches the persisted value, or while another save is in
  // flight.
  const handleSaveUserCommentNote = async () => {
    const analysisId = result?.analysis_id;
    if (analysisId === undefined) return;
    setSavingUserComment(true);
    try {
      const trimmed = userComment.trim();
      const res = await issueAnalysisApi.saveUserInputComment(
        analysisId,
        trimmed === '' ? null : trimmed,
      );
      setUserComment(res.user_input_comment ?? '');
      setUserCommentSavedAt(res.user_input_comment_updated_at);
      setUserCommentSavedBy(res.user_input_comment_updated_by);
      // Mirror back onto `result` so subsequent re-renders see the saved
      // value (a stale result would otherwise show the old comment after
      // a tab switch).
      setResult(prev => prev && ({
        ...prev,
        user_input_comment: res.user_input_comment,
        user_input_comment_updated_at: res.user_input_comment_updated_at,
        user_input_comment_updated_by: res.user_input_comment_updated_by,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save comment');
    } finally {
      setSavingUserComment(false);
    }
  };

  // The user-comment textbox is rendered as a slot by TicketDetailsContent,
  // appearing immediately below the "Full Extracted Screenshot Text"
  // section in both the intake preview and the post-analysis JIRA tab.
  const analysisIdForNote = result?.analysis_id;
  const persistedUserComment = result?.user_input_comment ?? '';
  const draftDiffersFromPersisted = userComment !== persistedUserComment;
  const tooLong = userComment.length > USER_COMMENT_MAX_LEN;
  const userCommentBox = (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
          Additional Context (sent with Analyze)
        </span>
        <span className={`text-[9px] ${tooLong ? 'text-red-600' : 'text-gray-400'}`}>
          {userComment.length} / {USER_COMMENT_MAX_LEN}
        </span>
      </div>
      <p className="mt-0.5 text-[9px] text-gray-400 italic">
        Add module/table details, business context, or anything missing from the JIRA
        ticket. Fed into the Phase 1, Phase 3, and Final Summary LLM steps and saved
        with this analysis for future retrieval.
      </p>
      <textarea
        className={`mt-1 w-full p-2 rounded border text-xs text-gray-800 leading-relaxed
                    focus:outline-none focus:ring-1
                    ${tooLong
                      ? 'bg-red-50 border-red-300 focus:ring-red-400'
                      : 'bg-white border-gray-300 focus:ring-blue-400'}`}
        rows={3}
        maxLength={USER_COMMENT_MAX_LEN + 200 /* allow slight overflow so paste shows length warning */}
        value={userComment}
        onChange={(e) => setUserComment(e.target.value)}
        placeholder="e.g. The ticket is about BL cargo handling; check the BL_CARGO and BL_VOYAGE tables for missing voyage links."
      />
      {analysisIdForNote !== undefined && (
        <div className="mt-1 flex items-center justify-between text-[10px] text-gray-500">
          <span>
            {userCommentSavedAt
              ? <>Last saved {new Date(userCommentSavedAt).toLocaleString()}
                  {userCommentSavedBy ? ` by ${userCommentSavedBy}` : ''}.</>
              : <span className="italic">Not yet saved on this analysis.</span>}
            {draftDiffersFromPersisted && !tooLong && (
              <span className="ml-1 text-amber-600 font-medium">Unsaved changes.</span>
            )}
          </span>
          <button
            type="button"
            onClick={handleSaveUserCommentNote}
            disabled={savingUserComment || !draftDiffersFromPersisted || tooLong}
            className="px-2 py-0.5 rounded border border-blue-300 bg-blue-50 text-blue-700
                       hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed
                       font-medium"
          >
            {savingUserComment ? 'Saving…' : 'Save note'}
          </button>
        </div>
      )}
    </div>
  );

  // Fetch available tables + recommendations for given modules.
  // Extracted as a callable function so the useEffect and retry button can share it.
  const fetchTablesRef = useRef<{ cancel: () => void } | null>(null);
  const fetchTables = useCallback((modules: string[], entityTypesArg?: string[], primaryEntityArg?: string) => {
    fetchTablesRef.current?.cancel();
    if (modules.length === 0) {
      setAvailableTables([]);
      setSelectedTables(new Set());
      setTableRecs(new Map());
      setTablesError(null);
      return;
    }
    let cancelled = false;
    fetchTablesRef.current = { cancel: () => { cancelled = true; } };
    setTablesLoading(true);
    setTablesError(null);
    setShowHidden(false);

    // Use caller-provided entity types (avoids closing over confirmedEntities,
    // which would cause fetchTables to get a new reference on every Bind click).
    const entityTypes = entityTypesArg ?? [];
    const primaryEntity = primaryEntityArg ?? 'Booking';

    Promise.all([
      // Send entity types so the backend can match table fields against them
      issueAnalysisApi.getTablesForModules(modules, entityTypes, ticketKey),
      // Get learned recommendations for each module+entity combo
      ...modules.map(mod =>
        issueAnalysisApi.getTableRecommendations(mod, primaryEntity).catch(() => ({ recommendations: [] }))
      ),
    ]).then(([tablesData, ...recResults]) => {
      if (cancelled) return;

      // Defense in depth: backend already dedups (case-insensitive on FQN
      // since the Issue-112 UI fix), but if anything slips through —
      // e.g. a future caller adds a code path that bypasses the dedup —
      // collapsing duplicates here keeps the table panel tidy and avoids
      // React's "duplicate key" warning that previously rendered the
      // same row twice.
      const seen = new Set<string>();
      const uniqueTables = tablesData.tables.filter((t) => {
        const key = t.fqn.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setAvailableTables(uniqueTables);
      setTablesError(null);

      // Merge all recommendations into a single map
      const recMap = new Map<string, TableRecommendation>();
      for (const result of recResults) {
        for (const rec of result.recommendations) {
          recMap.set(rec.table_fqn, rec);
        }
      }
      setTableRecs(recMap);

      // Smart auto-selection using three signals (in priority order):
      // 1. Learned history — auto_select/recommended tiers from past analyst choices
      // 2. Entity field match — tables that have columns matching detected entities
      // 3. Module default — if neither signal selects anything, include all module tables
      //
      // Tables flagged `excluded` by the backend (scratch / archive / log
      // surfaces) are never auto-selected by ANY signal — they are still
      // listed so an analyst can hand-pick one, but the dump showed they
      // produce only zero-row queries.
      const autoSelected = new Set<string>();

      // Signal 1: Module membership — check all non-excluded, non-hidden tables
      // that belong to the confirmed modules. History-based tiers are not
      // used as the primary signal because there isn't enough past data yet.
      for (const table of tablesData.tables) {
        if (table.excluded) continue;
        const rec = recMap.get(table.fqn);
        if (!rec || rec.tier !== 'hidden') {
          autoSelected.add(table.fqn);
        }
      }

      // Signal 2: Entity field matches — also check tables whose columns
      // directly match detected entity types, even if they are hidden tier.
      for (const table of tablesData.tables) {
        if (table.excluded) continue;
        if ((table.entity_matches?.length ?? 0) > 0) {
          autoSelected.add(table.fqn);
        }
      }

      setSelectedTables(autoSelected);
    }).catch((err) => {
      if (!cancelled) {
        setAvailableTables([]);
        setTableRecs(new Map());
        setTablesError(err instanceof Error ? err.message : 'Failed to load tables');
      }
    }).finally(() => {
      if (!cancelled) setTablesLoading(false);
    });
  }, [ticketKey]);

  // Auto-fetch tables when confirmed modules change. Latest entity list is read
  // from a ref so entity-only edits (Bind) do not re-trigger; module changes do.
  useEffect(() => {
    const entities = confirmedEntitiesRef.current;
    const entityTypes = Array.from(new Set(entities.map(e => e.entity_type)));
    const primaryEntity = entities[0]?.entity_type ?? 'Booking';
    fetchTables(confirmedModules, entityTypes, primaryEntity);
  }, [confirmedModules, fetchTables]);

  // Step 1: Run intake only (Phase 1) — detect entities + modules
  const runIntake = useCallback(async () => {
    const key = ticketKey.trim().toUpperCase();
    if (!key) return;
    if (!TICKET_KEY_RE.test(key)) {
      setError('Invalid ticket key format. Expected format: PROJ-123');
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    setResult(null);
    setIntakeResult(null);
    setCurrentStepId(null);
    try {
      const data = await issueAnalysisApi.intakeStream(
        key,
        controller.signal,
        (s) => {
          if (!controller.signal.aborted) {
            const stepId = mapProgressToStepId(s);
            if (stepId !== null) {
              setCurrentStepId(stepId);
            }
          }
        },
      );
      if (!controller.signal.aborted) {
        setIntakeResult(data.structured_ticket);
        setConfirmedModules(data.structured_ticket.modules);
        setConfirmedEntities(data.structured_ticket.entities);
      }
    } catch (err: unknown) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Intake failed');
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [ticketKey]);

  // Chat's ticket-key redirect: no saved analysis existed (autoIntakeSignal
  // set above), so run intake automatically — stops at the confirm screen,
  // same checkpoint as the manual "Analyze" button. Fires once per signal.
  useEffect(() => {
    if (!autoIntakeSignal) return;
    setAutoIntakeSignal(null);
    void runIntake();
  }, [autoIntakeSignal, runIntake]);

  // Step 2: Run full analysis with confirmed modules + entities.
  // `skipDataRetrieval=true` is the "Ignore Table details" path — bypasses
  // ERAX SQL + timeline lookups and grounds the analysis in JIRA + Support
  // Docs + Knowledge Bank only. Used for clarification-style tickets.
  const runAnalysis = useCallback(async (
    forceRefresh: boolean,
    modules?: string[],
    entities?: ExtractedEntity[],
    skipDataRetrieval = false,
  ): Promise<IssueAnalysisResponse | null> => {
    const key = ticketKey.trim().toUpperCase();
    if (!key) return null;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    setResult(null);
    setIntakeResult(null);
    setCurrentStepId(null);
    try {
      const modulesToSend = modules || confirmedModules;
      const entitiesToSend = entities || confirmedEntities;
      // In docs-only mode the table selection is irrelevant — don't send it.
      const tablesToSend = skipDataRetrieval
        ? undefined
        : (selectedTables.size > 0 ? Array.from(selectedTables) : undefined);
      // Carry forward the Support Docs / Knowledge Bank that intake already
      // fetched. The confirmed-path analyze skips re-running intake, so
      // without resending these the Support Docs tab renders empty even
      // though intake found documents.
      const intakeArtifacts = intakeResult
        ? {
            document_validations: intakeResult.document_validations,
            support_docs_answer: intakeResult.support_docs_answer,
            similar_issues: intakeResult.similar_issues,
            // Which attachments were read, skipped or failed. Without this
            // the confirmed path stores none, and an analysis built on 4 of
            // 16 attachments looks identical to one that read them all.
            image_extraction_results: intakeResult.image_extraction_results,
            // OCR + document text — without these the confirmed path stores
            // null and Compass forwarding never sees the screenshot text.
            extracted_image_text: intakeResult.extracted_image_text,
            extracted_document_text: intakeResult.extracted_document_text,
          }
        : undefined;
      const data = await issueAnalysisApi.analyzeStream(
        key,
        controller.signal,
        forceRefresh,
        modulesToSend.length > 0 ? modulesToSend : undefined,
        entitiesToSend.length > 0 ? entitiesToSend : undefined,
        tablesToSend,
        skipDataRetrieval,
        (s) => {
          if (!controller.signal.aborted) {
            const stepId = mapProgressToStepId(s);
            if (stepId !== null) {
              setCurrentStepId(stepId);
            }
          }
        },
        userComment.trim() === '' ? undefined : userComment,
        intakeArtifacts,
      );
      if (!controller.signal.aborted) {
        setResult(data);
        onAnalysisComplete?.(key);

        // Log table selections for learning (fire-and-forget). Skip in
        // docs-only mode — there's no table-selection signal worth recording.
        if (!skipDataRetrieval && availableTables.length > 0) {
          const modulesToLog = modules || confirmedModules;
          const entitiesToLog = entities || confirmedEntities;
          const primaryEntity = entitiesToLog[0]?.entity_type || 'Booking';
          for (const mod of modulesToLog) {
            const modTables = availableTables.filter(t => t.module === mod);
            if (modTables.length > 0) {
              issueAnalysisApi.logTableSelections(
                mod, primaryEntity, key,
                modTables.map(t => ({ table_fqn: t.fqn, selected: selectedTables.has(t.fqn) })),
              );
            }
          }
        }
        return data;
      }
      return null;
    } catch (err: unknown) {
      if (controller.signal.aborted) return null;
      setError(err instanceof Error ? err.message : 'Analysis failed');
      return null;
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [ticketKey, intakeResult, confirmedModules, confirmedEntities, selectedTables, availableTables, onAnalysisComplete]);

  const handleAnalyze = useCallback(() => runIntake(), [runIntake]);
  // A fresh analysis re-extracts from the JIRA text: analyst entity
  // corrections, seeded cards and the stored RCA for this ticket are all
  // replaced. That is legitimate — but it silently discarded hand-edited
  // values (e.g. Vessel split out of "ZHENOVA V.02614"), so it now asks.
  const handleReanalyze = useCallback(() => {
    const ok = window.confirm(
      'Re-analyze from scratch?\n\n' +
        'The ticket will be read again from JIRA and analysed as if new. ' +
        'This REPLACES what is stored for it:\n' +
        '  • entity values you corrected or added\n' +
        '  • the seeded SQL cards and their results\n' +
        '  • the current root-cause analysis\n\n' +
        'Do this when the ticket itself has changed. If you only want to ' +
        're-run the queries with your corrected entities, close this and use ' +
        'the SQL Investigation pane instead.',
    );
    if (ok) runAnalysis(true);
  }, [runAnalysis]);
  const handleConfirmAndAnalyze = useCallback(
    () => runAnalysis(true, confirmedModules, confirmedEntities),
    [runAnalysis, confirmedModules, confirmedEntities],
  );

  // Pre-analysis "Send ticket + SQL to Analysis":
  // 1. Run the analysis with skipDataRetrieval=true so Phase 2 is bypassed
  //    — the analyst-curated cards already cover that ground.
  // 2. Call analyze-with-data to swap the empty `sql_results` with the
  //    analyst datasets and regenerate the final RCA. The Final Analysis
  //    tab will pick up the freshly cached summary on first view.
  const handleSendPreAnalysisSql = useCallback(async (analystNotes: string) => {
    const data = await runAnalysis(true, confirmedModules, confirmedEntities, true);
    if (!data?.analysis_id) {
      throw new Error('Analysis could not be saved. Please retry.');
    }
    await issueAnalysisApi.analyzeWithData({
      analysis_id: data.analysis_id,
      analyst_notes: analystNotes || undefined,
      // Cards that never ran (rejected/skipped/timed out) must not reach the
      // analysis as genuine empty datasets — their 0 rows would read as
      // verified absence and can contradict the probe that DID return rows.
      datasets: preCards.filter((c) => !c.error).map((c) => ({ label: c.label, sql: c.sql, rows: c.rows, kind: c.kind })),
    });
  }, [runAnalysis, confirmedModules, confirmedEntities, preCards]);
  // "Ignore Table details" — clarification-style ticket. Skip ERAX SQL +
  // timeline retrieval and run the analysis from JIRA + Support Docs +
  // Knowledge Bank only. Always available, regardless of whether entities
  // were detected.
  const handleIgnoreTableDetails = useCallback(
    () => runAnalysis(true, confirmedModules, confirmedEntities, true),
    [runAnalysis, confirmedModules, confirmedEntities],
  );

  const handleAddModule = useCallback((override?: string) => {
    const mod = (override ?? moduleInput).trim();
    if (mod && !confirmedModules.includes(mod)) {
      setConfirmedModules(prev => [...prev, mod]);
      setModuleInput('');
      setShowModuleSuggestions(false);
      setModuleSuggestionIndex(0);
    }
  }, [moduleInput, confirmedModules]);

  const handleRemoveModule = useCallback((mod: string) => {
    setConfirmedModules(prev => prev.filter(m => m !== mod));
  }, []);

  // Module list is small and stable for the session, so cache on the
  // component and only fetch once.
  const ensureModulesLoaded = useCallback(async () => {
    if (allModules.length > 0) return;
    try {
      const { modules } = await issueAnalysisApi.listModules();
      setAllModules(modules);
    } catch (err) {
      console.warn('[IssueAnalysisPanel] Failed to load module list for typeahead', err);
    }
  }, [allModules.length]);

  // Case-insensitive substring match ranked by (match position, name
  // length, locale). The length tiebreaker ensures shorter names like
  // `CustomerMaster` rank above longer spaced variants like `Customer
  // Master Data Extract Report` so they survive the 15-item cap.
  const moduleSuggestions = useMemo(() => {
    const q = moduleInput.trim().toLowerCase();
    if (!q) return [] as string[];
    return allModules
      .filter(m => m.toLowerCase().includes(q) && !confirmedModules.includes(m))
      .sort((a, b) => {
        const aPos = a.toLowerCase().indexOf(q);
        const bPos = b.toLowerCase().indexOf(q);
        if (aPos !== bPos) return aPos - bPos;
        if (a.length !== b.length) return a.length - b.length;
        return a.localeCompare(b);
      })
      .slice(0, 15);
  }, [moduleInput, allModules, confirmedModules]);

  const handleAddEntity = useCallback(() => {
    const val = entityValueInput.trim();
    if (!val) return;
    const label = entityTypeInput.trim() || 'Identifier';
    const exists = confirmedEntities.some(
      e => e.entity_type === label && e.value === val,
    );
    if (exists) return;
    setConfirmedEntities(prev => [
      ...prev,
      {
        entity_type: label,
        value: val,
        confidence: 'high' as const,
        confidence_reason: 'Manually entered by analyst (no binding)',
        bindings: [],
      },
    ]);
    setEntityValueInput('');
  }, [entityTypeInput, entityValueInput, confirmedEntities]);

  const handleRemoveEntity = useCallback((entityType: string, value: string) => {
    setConfirmedEntities(prev =>
      prev.filter(e => !(e.entity_type === entityType && e.value === value)),
    );
  }, []);

  const handleEditEntityValue = useCallback((entityType: string, oldValue: string, newValue: string) => {
    const trimmed = newValue.trim();
    if (!trimmed || trimmed === oldValue) return;
    setConfirmedEntities(prev => {
      // Prevent duplicates — don't allow editing to a value that already exists under the same type
      const duplicate = prev.some(e => e.entity_type === entityType && e.value === trimmed);
      if (duplicate) return prev;
      return prev.map(e =>
        e.entity_type === entityType && e.value === oldValue
          ? { ...e, value: trimmed, confidence: 'high' as const, confidence_reason: 'Corrected by analyst' }
          : e,
      );
    });
  }, []);

  // Bind a value to a specific (table, column). Called from each field row in
  // the Fields panel. Creates a new entity with an initial binding, then
  // auto-proposes additional bindings on other selected tables whose
  // available field list contains the same column name (case-insensitive).
  // The target table is also auto-selected if it wasn't already — the user's
  // intent to query it is now explicit.
  const handleBindField = useCallback((tableFqn: string, column: string, value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;

    // Build the full set of bindings: the user's chosen column plus any
    // same-named column on other selected tables (case-insensitive match).
    const targetSelected = selectedTables.has(tableFqn);
    const columnLower = column.toLowerCase();
    const bindings: EntityBinding[] = [{ table_fqn: tableFqn, column }];
    for (const t of availableTables) {
      if (t.fqn === tableFqn) continue;
      if (!selectedTables.has(t.fqn)) continue;
      const match = (t.fields ?? []).find(f => f.toLowerCase() === columnLower);
      if (match) bindings.push({ table_fqn: t.fqn, column: match });
    }

    // Default label: reuse an existing entity's label if one already holds
    // this value, else fall back to the column name. The user can rename.
    const existing = confirmedEntities.find(e => e.value === trimmed);
    const label = existing?.entity_type ?? column;

    setConfirmedEntities(prev => {
      const idx = prev.findIndex(e => e.value === trimmed);
      if (idx === -1) {
        return [...prev, {
          entity_type: label,
          value: trimmed,
          confidence: 'high' as const,
          confidence_reason: 'Manually bound by analyst',
          bindings,
        }];
      }
      // Merge new bindings into the existing entity, deduping by (fqn, column).
      const current = prev[idx].bindings ?? [];
      const seen = new Set(current.map(b => `${b.table_fqn}::${b.column.toLowerCase()}`));
      const merged = [...current];
      for (const b of bindings) {
        const key = `${b.table_fqn}::${b.column.toLowerCase()}`;
        if (!seen.has(key)) { merged.push(b); seen.add(key); }
      }
      const next = [...prev];
      next[idx] = { ...prev[idx], bindings: merged };
      return next;
    });

    // Make sure the target table is ticked. Leave other auto-picked tables as
    // they were — the user can deselect.
    if (!targetSelected) {
      setSelectedTables(prev => {
        const next = new Set(prev);
        next.add(tableFqn);
        return next;
      });
    }
  }, [availableTables, selectedTables, confirmedEntities]);

  const handleCancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setLoading(false);
  }, []);

  return (
    <div className="space-y-4">
      {/* Search bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={ticketKey}
            onChange={e => setTicketKey(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !loading && handleAnalyze()}
            placeholder="Enter JIRA ticket key (e.g. PROJ-123)"
            aria-label="JIRA ticket key"
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleAnalyze}
            disabled={loading || !ticketKey.trim()}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Analyzing...' : 'Analyze'}
          </button>
          <button
            onClick={() => setShowFlowchartModal(true)}
            className="px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-1"
            title="View analysis pipeline"
            aria-label="Show analysis flowchart"
          >
            <HelpCircle size={16} />
          </button>
        </div>
      </div>

      {loading && (
        <div className="space-y-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <AnalysisFlowchart currentStepId={currentStepId} isPreview={false} />
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" role="alert">
          {error}
        </div>
      )}

      {/* Module Confirmation Step — shown after intake, before full analysis */}
      {intakeResult && !loading && !result && (
        <div className="space-y-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
            <AlertTriangle size={16} />
            Confirm Entities &amp; Modules Before Analysis
          </div>
          <p className="text-xs text-amber-700">
            The following entities and modules were auto-detected from <strong>{intakeResult.ticket_key}</strong>.
            Please verify the entities (booking numbers, BL numbers, etc.) and modules, then confirm to run the full analysis.
          </p>

          {/* Summary */}
          <div className="text-xs text-gray-600">
            <strong>Summary:</strong> {intakeResult.summary}
          </div>

          {/* JIRA Ticket Details — collapsible, shown before entity/module confirmation */}
          <JiraDetailsPreview ticket={intakeResult} userCommentBox={userCommentBox} onModuleClick={setSelectedModuleForGraph} />

          {/* Editable Entities */}
          <div>
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
              Entities (verify, edit, or add — click value to edit, &times; to remove)
            </span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {confirmedEntities.map((e) => (
                <EntityPill
                  key={`${e.entity_type}-${e.value}`}
                  entity={e}
                  onRemove={() => handleRemoveEntity(e.entity_type, e.value)}
                  onEditValue={(newVal) => handleEditEntityValue(e.entity_type, e.value, newVal)}
                />
              ))}
            </div>
            {confirmedEntities.length === 0 && (
              <p className="text-[10px] text-gray-400 italic mt-1">
                No entities detected. Expand a table below and use its Bind column to attach a value to a specific field —
                or use the fallback form below to add an identifier without a column binding.
              </p>
            )}
            {/* Fallback: add an identifier without explicit bindings. The
                 backend retrieval layer falls back to heuristic column matching
                 in that case. Primary flow is now the Bind input in each
                 table's Fields panel. */}
            <div className="flex items-center gap-1 mt-2">
              <input
                type="text"
                value={entityTypeInput}
                onChange={e => setEntityTypeInput(e.target.value)}
                placeholder="Label (optional)..."
                aria-label="Entity label"
                className="text-[10px] px-1.5 py-0.5 border border-gray-300 rounded w-32 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <input
                type="text"
                value={entityValueInput}
                onChange={e => setEntityValueInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddEntity()}
                placeholder="Value..."
                className="text-[10px] px-1.5 py-0.5 border border-gray-300 rounded w-40 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <button
                onClick={handleAddEntity}
                disabled={!entityValueInput.trim()}
                title="Add without explicit column binding (retrieval falls back to heuristic matching)"
                className="text-[10px] px-2 py-0.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 transition-colors"
              >
                + Add (no binding)
              </button>
            </div>
          </div>

          {/* Editable Modules */}
          <div>
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Modules (click to remove, type to add)</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {confirmedModules.map((m) => (
                <button
                  key={m}
                  onClick={() => handleRemoveModule(m)}
                  title="Click to remove"
                  className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded border border-blue-300 hover:bg-red-100 hover:text-red-800 hover:border-red-300 transition-colors cursor-pointer"
                >
                  {m} &times;
                </button>
              ))}
              <div className="flex items-center gap-1 relative">
                <input
                  type="text"
                  value={moduleInput}
                  onFocus={() => {
                    ensureModulesLoaded();
                    if (moduleInput.trim()) setShowModuleSuggestions(true);
                  }}
                  // onBlur hides the dropdown when focus actually leaves the
                  // input (tab-away, click outside). When the user clicks a
                  // suggestion, the <li>'s onMouseDown calls preventDefault,
                  // so focus stays on the input and this handler never fires.
                  onBlur={() => setShowModuleSuggestions(false)}
                  onChange={e => {
                    setModuleInput(e.target.value);
                    setShowModuleSuggestions(true);
                    setModuleSuggestionIndex(0);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setShowModuleSuggestions(true);
                      setModuleSuggestionIndex(i =>
                        Math.min(i + 1, Math.max(0, moduleSuggestions.length - 1))
                      );
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setModuleSuggestionIndex(i => Math.max(0, i - 1));
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      const pick = moduleSuggestions[moduleSuggestionIndex];
                      handleAddModule(pick); // pick is undefined → falls back to raw input
                    } else if (e.key === 'Escape') {
                      setShowModuleSuggestions(false);
                    }
                  }}
                  placeholder="Add module..."
                  className="text-[10px] px-1.5 py-0.5 border border-gray-300 rounded w-28 focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
                <button
                  onClick={() => handleAddModule()}
                  disabled={!moduleInput.trim()}
                  className="text-[10px] px-1.5 py-0.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  +
                </button>
                {showModuleSuggestions && moduleSuggestions.length > 0 && (
                  <ul
                    className="absolute z-20 left-0 top-full mt-0.5 min-w-[10rem] max-h-48 overflow-y-auto bg-white border border-gray-300 rounded shadow-lg"
                    role="listbox"
                  >
                    {moduleSuggestions.map((s, idx) => (
                      <li
                        key={s}
                        role="option"
                        aria-selected={idx === moduleSuggestionIndex}
                        onMouseDown={e => {
                          // Use onMouseDown so the click registers before the
                          // input's onBlur fires and hides the dropdown.
                          e.preventDefault();
                          handleAddModule(s);
                        }}
                        onMouseEnter={() => setModuleSuggestionIndex(idx)}
                        className={`text-[10px] px-2 py-1 cursor-pointer ${
                          idx === moduleSuggestionIndex
                            ? 'bg-blue-100 text-blue-900'
                            : 'text-gray-800 hover:bg-gray-100'
                        }`}
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Suggested modules (medium/low relevance — not auto-selected) */}
          {intakeResult?.detected_modules && (() => {
            const suggested = intakeResult.detected_modules!.filter(
              m => m.relevance !== 'high' && !confirmedModules.includes(m.name)
            );
            if (suggested.length === 0) return null;
            return (
              <div>
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  Suggested modules (click + to add)
                </span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {suggested.map(m => (
                    <button
                      key={m.name}
                      onClick={() => handleAddModule(m.name)}
                      title={`Relevance: ${m.relevance} · Source: ${m.source}`}
                      className={`text-[10px] px-1.5 py-0.5 rounded border cursor-pointer transition-colors ${
                        m.relevance === 'medium'
                          ? 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100'
                          : 'bg-gray-50 text-gray-500 border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      + {m.name}
                      <span className="ml-1 opacity-60">{m.relevance}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {confirmedModules.length === 0 && (
            <p className="text-xs text-red-600">Please add at least one module before proceeding.</p>
          )}

          {/* Table Auto-Selection Summary + Advanced Override */}
          <TableAutoSelection
            availableTables={availableTables}
            selectedTables={selectedTables}
            setSelectedTables={setSelectedTables}
            tableRecs={tableRecs}
            showHidden={showHidden}
            setShowHidden={setShowHidden}
            tablesLoading={tablesLoading}
            tablesError={tablesError}
            confirmedModules={confirmedModules}
            fetchTables={fetchTables}
            onBindField={handleBindField}
            entities={confirmedEntities}
            ticketKey={ticketKey}
          />

          {/* SQL Investigation — pre-analysis variant. Lets the analyst
              inspect LRP data and curate the SQL evidence BEFORE clicking
              Confirm & Run Analysis. "Send ticket + SQL to Analysis"
              skips Phase 2 entirely and feeds the curated bundle straight
              into the LLM. */}
          <SqlInvestigation
            ticketKey={intakeResult.ticket_key}
            modules={confirmedModules}
            entities={confirmedEntities}
            availableTables={availableTables}
            availableTablesLoading={tablesLoading}
            onRefreshTables={() => fetchTables(
              confirmedModules,
              Array.from(new Set(confirmedEntities.map((e) => e.entity_type))),
              confirmedEntities[0]?.entity_type ?? 'Booking',
            )}
            cards={preCards}
            setCards={setPreCards}
            onSend={handleSendPreAnalysisSql}
            sendLabel="Send ticket + SQL to Analysis"
            title="SQL Investigation"
            subtitle="Auto-seeded by LLM from your modules, entities and selected tables. Edit any card or add new ones, then send ticket + SQL to Final Analysis."
            defaultExpanded
            seeding={seedingPreCards}
            seedingError={preCardsError}
            seedingSkipped={preCardsSkipped}
            semanticSuggestions={preCardsSemantic}
            structuralSuggestions={preCardsStructural}
            onAddTable={(fqn) =>
              setSelectedTables((prev) => {
                const next = new Set(prev);
                next.add(fqn);
                return next;
              })
            }
          />

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleConfirmAndAnalyze}
              disabled={confirmedModules.length === 0 || seedingPreCards}
              title={seedingPreCards ? 'SQL cards are still being generated — wait for seeding to finish' : undefined}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Confirm &amp; Run Analysis
            </button>
            <button
              onClick={handleIgnoreTableDetails}
              disabled={confirmedModules.length === 0 || seedingPreCards}
              title={
                seedingPreCards
                  ? 'SQL cards are still being generated — wait for seeding to finish'
                  : 'Skip ERAX SQL and Event Timeline lookups. Use this for clarification-style tickets where no Booking/BL/Container number is referenced — the analysis will be grounded in JIRA + Support Docs + Knowledge Bank only.'
              }
              className="px-4 py-2 text-sm font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded-lg hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Ignore Table details
            </button>
            <button
              onClick={() => { setIntakeResult(null); setConfirmedModules([]); setConfirmedEntities([]); setAvailableTables([]); setSelectedTables(new Set()); setTablesError(null); }}
              className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-8 text-sm text-gray-500" aria-live="polite">
          <div className="animate-spin inline-block w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full mb-2" />
          <p>{intakeResult ? 'Running full analysis with confirmed modules...' : 'Running intake...'}</p>
          <p className="text-xs text-gray-400 mt-1">Phase 1: Intake &rarr; Phase 2: Retrieval &rarr; Phase 3: Analysis</p>
          <button
            onClick={handleCancel}
            className="mt-3 px-3 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {result && <AnalysisResult result={result} onReanalyze={handleReanalyze} loading={loading} userCommentBox={userCommentBox} onModuleClick={setSelectedModuleForGraph} />}

      {/* Flowchart Modal */}
      <FlowchartModal isOpen={showFlowchartModal} onClose={() => setShowFlowchartModal(false)} />

      {/* Module Graph Modal */}
      <ModuleGraphModal
        isOpen={selectedModuleForGraph !== null}
        moduleName={selectedModuleForGraph}
        onClose={() => setSelectedModuleForGraph(null)}
      />
    </div>
  );
}

// ── Data Tabs ─────────────────────────────────────────────────────────────

type DataTab = 'jira' | 'erax' | 'timeline' | 'docs' | 'final';

const DATA_TABS: { id: DataTab; label: string; icon: React.ReactNode }[] = [
  // Support Docs is shown right after JIRA because its content, together with
  // the ticket text, feeds the Detected Modules list. Having the two source
  // tabs adjacent makes that dependency visible to the analyst.
  { id: 'jira',     label: 'JIRA Ticket',     icon: <FileText size={12} /> },
  { id: 'docs',     label: 'Support Docs',     icon: <BookOpen size={12} /> },
  { id: 'erax',     label: 'ERAX Data',       icon: <Database size={12} /> },
  { id: 'timeline', label: 'Event Timeline',   icon: <Clock size={12} /> },
  { id: 'final',    label: 'Final Analysis',   icon: <Target size={12} /> },
];

/** Tab 1: JIRA ticket structured data. */
function JiraTab({ result, userCommentBox, cards, setCards, onSentToAnalysis, onModuleClick, onOpenEntityTools }: {
  result: IssueAnalysisResponse;
  userCommentBox?: React.ReactNode;
  cards: SqlCard[];
  setCards: React.Dispatch<React.SetStateAction<SqlCard[]>>;
  onSentToAnalysis?: () => void;
  onModuleClick?: (moduleName: string) => void;
  /** Opens the floating Entity Types window, which the parent owns so it
   *  survives a tab switch. */
  onOpenEntityTools?: () => void;
}) {
  const { structured_ticket: ticket } = result;

  // Analyst entity corrections — editable in the Extracted Entities table,
  // persisted on the stored analysis, and fed to the refine pane below.
  const [entities, setEntities] = useState<ExtractedEntity[]>(ticket.entities ?? []);
  useEffect(() => { setEntities(ticket.entities ?? []); }, [result.analysis_id]);
  const persistEntities = useCallback((next: ExtractedEntity[]) => {
    setEntities(next);
    if (result.analysis_id) {
      issueAnalysisApi.updateEntities(result.analysis_id, next).catch(() => {
        /* best-effort — corrections still apply to this session */
      });
    }
  }, [result.analysis_id]);
  const entityActions: EntityTableActions = {
    onEditValue: (i, v) => persistEntities(entities.map((e, j) =>
      j === i ? { ...e, value: v, confidence: 'high' as const, confidence_reason: 'Corrected by analyst' } : e)),
    onRemove: (i) => persistEntities(entities.filter((_, j) => j !== i)),
    onAdd: (t, v) => {
      if (entities.some((e) => e.entity_type === t && e.value === v)) return;
      persistEntities([...entities, {
        entity_type: t, value: v,
        confidence: 'high' as const,
        confidence_reason: 'Manually entered by analyst',
        bindings: [],
      }]);
    },
    // Two effects, deliberately: raise it here so this analysis proceeds
    // without the doubt, AND record it centrally so later tickets inherit
    // the judgement. Without the second, every ticket re-asks the same
    // question — which is the whole complaint.
    onConfirm: (i) => {
      const e = entities[i];
      if (!e) return;
      persistEntities(entities.map((x, j) =>
        j === i
          ? { ...x, confidence: 'high' as const, confidence_reason: 'Confirmed by analyst' }
          : x));
      const ticketKey = result.structured_ticket?.ticket_key;
      if (ticketKey) {
        issueAnalysisApi
          .confirmEntityValue(ticketKey, e.entity_type, e.value)
          .catch(() => {
            /* best-effort — the per-ticket promotion above still applies */
          });
      }
    },
  };

  const handleSend = useCallback(async (analystNotes: string) => {
    if (!result.analysis_id) {
      throw new Error('Analysis is not yet saved. Run Confirm & Run Analysis first.');
    }
    await issueAnalysisApi.analyzeWithData({
      analysis_id: result.analysis_id,
      analyst_notes: analystNotes || undefined,
      // Same guard as the pre-analysis path: failed cards are UI-visible
      // but never analysis evidence.
      datasets: cards.filter((c) => !c.error).map((c) => ({ label: c.label, sql: c.sql, rows: c.rows, kind: c.kind })),
    });
    onSentToAnalysis?.();
  }, [result.analysis_id, cards, onSentToAnalysis]);

  // Wrap the ticket details in HintCaptureMenu so analysts can select any
  // word in the Description, AI Summary, or Extracted Entities table and
  // record a correction (module / entity-type / table-field binding) that
  // will be applied automatically to every future ticket.
  return (
    <HintCaptureMenu
      ticketKey={ticket.ticket_key}
      availableModules={ticket.modules ?? []}
    >
      <TicketDetailsContent ticket={ticket} keyPrefix="tab" showModulesAndEntities userCommentBox={userCommentBox} onModuleClick={onModuleClick} entitiesOverride={entities} entityActions={entityActions} onOpenEntityTools={onOpenEntityTools} />
      <SqlInvestigation
        ticketKey={ticket.ticket_key}
        modules={ticket.modules ?? []}
        entities={entities}
        cards={cards}
        setCards={setCards}
        onSend={result.analysis_id ? handleSend : undefined}
        title="SQL Investigation (refine)"
        subtitle="Edit auto-generated cards or add new SQL — sending overwrites the Final Analysis with your curated evidence."
        defaultExpanded
      />
    </HintCaptureMenu>
  );
}

/**
 * Tab 2: ERAX SQL query output. Mirrors the live Saved Results cards from
 * the SQL Investigation section so analyst edits are reflected immediately,
 * with a fallback to the original Phase-2 `sql_results` rendering when the
 * cards store is empty (defensive — should always be seeded on mount).
 */
function EraxTab({ result, cards }: { result: IssueAnalysisResponse; cards: SqlCard[] }) {
  const dataCards = cards.filter((c) => c.kind === 'data');
  const { retrieval_results: retrieval } = result;
  const sqlResults = retrieval.sql_results ?? [];

  if (dataCards.length === 0 && sqlResults.length === 0) {
    return <p className="text-xs text-gray-500 italic py-4 text-center">No entities to query. Extract entities from the JIRA ticket first.</p>;
  }

  if (dataCards.length === 0) {
    // Fallback to the legacy renderer if cards haven't seeded yet.
    return (
      <div className="space-y-3">
        {sqlResults.map((r) => (
          <SqlResultCard key={`sql-${r.entity_type}-${r.entity_value}`} result={r} />
        ))}
      </div>
    );
  }

  const editedCount = dataCards.filter((c) => c.source === 'manual' || c.edited).length;
  return (
    <div className="space-y-3">
      {editedCount > 0 && (
        <div className="p-2 bg-indigo-50 rounded border border-indigo-200 text-[11px] text-indigo-700">
          Showing live SQL Investigation cards ({editedCount} analyst-edited or added).
        </div>
      )}
      {dataCards.map((c) => <CardBlock key={c.id} card={c} />)}
    </div>
  );
}

/** Debug: collapsible list of SQL queries attempted for timeline generation. */
function TimelineQueriesDebug({ queries }: { queries: string[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={`${open ? 'Hide' : 'Show'} timeline queries`}
        className="flex items-center gap-1 text-[10px] font-semibold text-blue-600 hover:text-blue-800"
      >
        {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
        Queries Attempted ({queries.length})
      </button>
      {open && (
        <pre className="mt-1 p-2 bg-gray-900 text-green-300 rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
          {queries.join(';\n\n')}
        </pre>
      )}
    </div>
  );
}

/** Tab 3: Event timeline (for Booking, BL, Container). */
function TimelineTab({ result, cards }: { result: IssueAnalysisResponse; cards: SqlCard[] }) {
  const { retrieval_results: retrieval } = result;
  const timelines = retrieval.timelines ?? [];
  const timelineCards = cards.filter((c) => c.kind === 'timeline');

  const createdDate = result.structured_ticket?.created
    ? new Date(result.structured_ticket.created)
    : null;
  const ageWarning = createdDate
    ? (Date.now() - createdDate.getTime()) > TIMELINE_AGE_LIMIT_MS
    : false;

  const AgeWarningBanner = ageWarning ? (
    <p style={{ fontSize: 10, color: '#dc2626', margin: '0 0 6px 0' }}>
      {TIMELINE_AGE_WARNING}
    </p>
  ) : null;

  const TimelineCardSection = timelineCards.length > 0 ? (
    <div className="space-y-1">
      <span className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wide">
        Analyst SQL Timeline Queries ({timelineCards.length})
      </span>
      {timelineCards.map((c) => <CardBlock key={c.id} card={c} />)}
    </div>
  ) : null;

  if (timelines.length === 0 && timelineCards.length === 0) {
    return (
      <div>
        {AgeWarningBanner}
        <p className="text-xs text-gray-500 italic py-4 text-center">
          No timeline-eligible entities (Booking, BL, Container) found in this ticket.
        </p>
      </div>
    );
  }

  if (timelines.length === 0) {
    return (
      <div className="space-y-2">
        {AgeWarningBanner}
        {TimelineCardSection}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {AgeWarningBanner}
      {timelines.map((t) => (
        <div key={`tl-${t.entity_type}-${t.entity_value}`} className="p-2 bg-gray-50 rounded text-xs">
          <div className="flex items-center gap-2">
            <Clock size={12} className={t.generated ? 'text-green-500' : 'text-gray-400'} />
            <span className="font-medium text-gray-800">{t.entity_type}: <span className="font-mono text-blue-700">{t.entity_value}</span></span>
            {t.generated && <span className="text-[10px] text-green-600">{t.events?.length ?? 0} events</span>}
          </div>
          {t.message && <p className="mt-1 text-gray-500 italic">{t.message}</p>}
          {t.generated && t.events?.length > 0 && (
            <div className="mt-2 space-y-1">
              {t.events.map((ev, i) => (
                <div key={`ev-${i}-${ev.timestamp}`} className="flex gap-2 text-[10px] pl-2 border-l-2 border-blue-200">
                  <span className="font-mono text-gray-400 whitespace-nowrap">{ev.timestamp}</span>
                  <span className="font-semibold text-gray-700">{ev.event_type}</span>
                  {ev.state_transition && <span className="text-blue-600">{ev.state_transition}</span>}
                  <span className="text-gray-500">{ev.actor}</span>
                </div>
              ))}
            </div>
          )}
          {/* Debug: Show attempted SQL queries */}
          {t.queries_attempted && t.queries_attempted.length > 0 && (
            <TimelineQueriesDebug queries={t.queries_attempted} />
          )}
        </div>
      ))}
      {TimelineCardSection}
    </div>
  );
}

/** Tab 4: Support documentation and knowledge bank matches. */
function DocsTab({ result }: { result: IssueAnalysisResponse }) {
  const { retrieval_results: retrieval } = result;
  const answer = retrieval.support_docs_answer;
  const docs = retrieval.document_validations ?? [];
  const similar = retrieval.similar_issues ?? [];
  const [showSources, setShowSources] = useState(false);

  if (!answer && docs.length === 0 && similar.length === 0) {
    return <p className="text-xs text-gray-500 italic py-4 text-center">No support documentation or past issues matched.</p>;
  }

  return (
    <div className="space-y-3">
      {/* LLM-Synthesized Answer (same quality as Chat) */}
      {answer && (
        <div>
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">AI-Generated Analysis</span>
          <div className="mt-1 prose prose-sm max-w-none text-xs leading-relaxed">
            <MarkdownRenderer content={answer} />
          </div>
        </div>
      )}

      {/* Collapsible source documents */}
      {docs.length > 0 && (
        <div>
          <button
            onClick={() => setShowSources(!showSources)}
            className="flex items-center gap-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700 transition-colors"
          >
            {showSources ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
            Sources ({docs.length} documents)
          </button>
          {showSources && (
            <div className="mt-1 space-y-1">
              {docs.map((doc, i) => (
                <div key={`doc-${i}-${doc.source_path}`} className="p-2 bg-gray-50 rounded text-xs">
                  <div className="flex items-center gap-2">
                    <BookOpen size={12} className="text-indigo-500" />
                    <span className="font-medium text-gray-800">{doc.doc_title}</span>
                    <span className="text-[10px] font-mono px-1 py-0.5 bg-indigo-50 rounded text-indigo-700">
                      {Math.round(doc.score * 100)}% relevant
                    </span>
                  </div>
                  {doc.section_hierarchy?.length > 0 && (
                    <p className="text-[10px] text-gray-400 mt-0.5">{doc.section_hierarchy.join(' > ')}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Knowledge Bank Matches */}
      {similar.length > 0 && (
        <div>
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Knowledge Bank Matches</span>
          {similar.map((issue) => (
            <div key={`kb-${issue.reference}`} className="mt-1 p-2 bg-gray-50 rounded text-xs">
              <div className="flex items-center gap-2">
                <BookOpen size={12} className="text-blue-500" />
                <span className="font-medium text-gray-800">{issue.reference}</span>
                <span className="text-[10px] font-mono px-1 py-0.5 bg-blue-50 rounded text-blue-700">
                  {Math.round(issue.similarity_score * 100)}% match
                </span>
              </div>
              <p className="mt-1 text-gray-600">{issue.summary}</p>
              <p className="text-gray-500">Root cause: {issue.root_cause}</p>
              <p className="text-gray-500">Resolution: {issue.resolution}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Maximum analyst-comment length, mirrored from the backend's
 *  `chk_analyst_comment_len` CHECK constraint. */
const ANALYST_COMMENT_MAX_LEN = 1000;

function formatAnalystCommentTimestamp(s: string | null | undefined): string {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Editable free-text final comment shown next to the RCA. Analyst can
 * write 5–6 lines (≤1000 chars) summarising their final take. Edits are
 * attributed via the Firebase auth identity server-side; the textarea
 * here only captures the text. A re-run of the analysis preserves the
 * comment (unlike `verified_at`).
 */
function AnalystCommentBox(props: {
  analysisId: number;
  initialComment: string | null;
  initialUpdatedAt: string | null;
  initialUpdatedBy: string | null;
}) {
  const { analysisId, initialComment, initialUpdatedAt, initialUpdatedBy } = props;
  const [persisted, setPersisted] = useState<string>(initialComment ?? '');
  const [draft, setDraft] = useState<string>(initialComment ?? '');
  const [updatedAt, setUpdatedAt] = useState<string | null>(initialUpdatedAt);
  const [updatedBy, setUpdatedBy] = useState<string | null>(initialUpdatedBy);
  const [expanded, setExpanded] = useState<boolean>(Boolean(initialComment));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed when the analysis_id changes (e.g. switching tickets or after
  // a re-analysis returns a new payload).
  useEffect(() => {
    setPersisted(initialComment ?? '');
    setDraft(initialComment ?? '');
    setUpdatedAt(initialUpdatedAt);
    setUpdatedBy(initialUpdatedBy);
    setExpanded(Boolean(initialComment));
    setError(null);
  }, [analysisId, initialComment, initialUpdatedAt, initialUpdatedBy]);

  const dirty = draft !== persisted;
  const tooLong = draft.length > ANALYST_COMMENT_MAX_LEN;
  const charsLeft = ANALYST_COMMENT_MAX_LEN - draft.length;

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const next = draft.trim() === '' ? null : draft;
      const res = await issueAnalysisApi.saveAnalystComment(analysisId, next);
      setPersisted(res.analyst_comment ?? '');
      setDraft(res.analyst_comment ?? '');
      setUpdatedAt(res.analyst_comment_updated_at ?? null);
      setUpdatedBy(res.analyst_comment_updated_by ?? null);
      if (!res.analyst_comment) setExpanded(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [analysisId, draft]);

  const handleCancel = useCallback(() => {
    setDraft(persisted);
    setError(null);
  }, [persisted]);

  if (!expanded) {
    return (
      <div className="mt-2 px-3 py-2 border border-gray-200 rounded text-xs flex items-center justify-between bg-gray-50/50">
        <span className="flex items-center gap-2 text-gray-600">
          <MessageSquare size={14} className="text-gray-400" />
          Analyst Final Comments
          <span className="text-gray-400">— none yet</span>
        </span>
        <button
          onClick={() => setExpanded(true)}
          className="text-primary-600 hover:text-primary-700 font-medium"
        >
          + Add final comment
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 border border-amber-200 rounded bg-amber-50/40">
      <div className="flex items-center justify-between px-3 py-2 border-b border-amber-200">
        <span className="flex items-center gap-2 text-xs font-semibold text-amber-800">
          <MessageSquare size={14} />
          Analyst Final Comments
        </span>
        {updatedAt && (
          <span className="text-[10px] text-amber-700/80">
            Last edited {formatAnalystCommentTimestamp(updatedAt)}
            {updatedBy ? ` by ${updatedBy}` : ''}
          </span>
        )}
      </div>
      <div className="p-3 space-y-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add your final take on this ticket — 5 to 6 lines is the typical sweet spot."
          rows={5}
          spellCheck
          className={`w-full text-xs border rounded p-2 font-sans focus:outline-none focus:ring-2 ${
            tooLong
              ? 'border-red-300 focus:ring-red-400'
              : 'border-gray-300 focus:ring-amber-400'
          }`}
        />
        <div className="flex items-center justify-between gap-2">
          <span
            className={`text-[10px] ${
              tooLong ? 'text-red-600 font-semibold' : 'text-gray-500'
            }`}
          >
            {draft.length} / {ANALYST_COMMENT_MAX_LEN}
            {tooLong && (
              <span className="ml-1">
                ({Math.abs(charsLeft)} over the limit)
              </span>
            )}
          </span>
          <div className="flex items-center gap-1">
            {persisted !== '' && draft.trim() === '' && (
              <span className="text-[10px] text-amber-700 mr-1 flex items-center gap-1">
                <Trash2 size={10} />
                Saving will clear this comment
              </span>
            )}
            <button
              onClick={handleCancel}
              disabled={!dirty || saving}
              className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!dirty || saving || tooLong}
              className="flex items-center gap-1 px-3 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
              Save
            </button>
          </div>
        </div>
        {error && (
          <div className="flex items-center gap-1 text-[11px] text-red-700">
            <AlertCircle size={12} />
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

/** Colors for the issue classification badge on the Final Analysis tab.
 *  Labels are the canonical values stored in final_classification. */
const CLASSIFICATION_STYLES: Record<string, string> = {
  'Bug': 'text-red-700 bg-red-50 border-red-200',
  'Clarification': 'text-blue-700 bg-blue-50 border-blue-200',
  'Gap': 'text-amber-700 bg-amber-50 border-amber-200',
  'Service Request': 'text-purple-700 bg-purple-50 border-purple-200',
  'Others': 'text-gray-600 bg-gray-50 border-gray-200',
};

/** Tab 5: Final Analysis — LLM-generated plain-English summary of all data. */
function FinalAnalysisTab({ result }: { result: IssueAnalysisResponse }) {
  const [summary, setSummary] = useState<string | null>(null);
  const [classification, setClassification] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Verification state is seeded from the server payload and updated optimistically
  // when the analyst clicks the Verify button. Prop changes (e.g. after a re-analysis
  // clears the flag) re-seed via the effect below.
  const [verifiedAt, setVerifiedAt] = useState<string | null>(result.verified_at ?? null);
  const [verifiedBy, setVerifiedBy] = useState<string | null>(result.verified_by ?? null);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  useEffect(() => {
    setVerifiedAt(result.verified_at ?? null);
    setVerifiedBy(result.verified_by ?? null);
    setVerifyError(null);
  }, [result.analysis_id, result.verified_at, result.verified_by]);

  const toggleVerified = useCallback(async () => {
    if (!result.analysis_id) return;
    const nextVerified = !verifiedAt;
    setVerifying(true);
    setVerifyError(null);
    try {
      const res = await issueAnalysisApi.setVerified(result.analysis_id, nextVerified);
      setVerifiedAt(nextVerified ? (res.verified_at ?? new Date().toISOString()) : null);
      setVerifiedBy(nextVerified ? (res.verified_by ?? null) : null);
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'Failed to update verification');
    } finally {
      setVerifying(false);
    }
  }, [result.analysis_id, verifiedAt]);

  const generate = useCallback(async (forceRefresh = false) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const data = await issueAnalysisApi.generateFinalSummary(result, controller.signal, forceRefresh);
      if (!controller.signal.aborted) {
        setSummary(data.summary);
        setClassification(data.classification ?? null);
      }
    } catch (err: unknown) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to generate summary');
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [result]);

  // Auto-generate on first render (only if analysis was persisted)
  useEffect(() => {
    if (!summary && !loading && !error && result.analysis_id) {
      generate();
    }
    return () => { abortRef.current?.abort(); };
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-3">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-xs text-gray-500">Generating final analysis summary...</p>
        <p className="text-[10px] text-gray-400">Synthesizing data from all tabs via LLM</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3 py-4">
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
        <button
          onClick={() => generate()}
          className="text-xs text-blue-600 hover:text-blue-800 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!summary && !result.analysis_id) {
    return (
      <p className="text-xs text-gray-500 italic py-4 text-center">
        Final summary unavailable — analysis was not saved to the database. Try re-analyzing.
      </p>
    );
  }

  if (!summary) return null;

  const verifiedBadge = verifiedAt ? (
    <span className="text-[10px] text-green-700 bg-green-50 border border-green-200 rounded px-1.5 py-0.5">
      Verified {verifiedBy ? `by ${verifiedBy}` : ''}
      {' · '}
      {new Date(verifiedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
    </span>
  ) : null;

  // Cached summaries generated before v1.57.0 have no classification —
  // the badge appears after the analyst clicks Regenerate.
  const classificationBadge = classification ? (
    <span
      title="Issue classification determined by the AI final analysis"
      className={`text-[10px] font-semibold border rounded px-1.5 py-0.5 ${
        CLASSIFICATION_STYLES[classification] ?? CLASSIFICATION_STYLES['Others']
      }`}
    >
      {classification}
    </span>
  ) : null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">AI-Generated Summary</span>
          {classificationBadge}
        </div>
        <div className="flex items-center gap-2">
          {verifiedBadge}
          {result.analysis_id && (
            <button
              onClick={toggleVerified}
              disabled={verifying}
              title={
                verifiedAt
                  ? 'Click to unmark this RCA as verified'
                  : 'Mark this RCA as verified — confirms the analyst resolved the ticket with this root cause. Verified analyses feed the Q&A learning loop.'
              }
              className={`text-[10px] px-2 py-0.5 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                verifiedAt
                  ? 'text-gray-600 border-gray-300 hover:bg-gray-50'
                  : 'text-green-700 border-green-300 hover:bg-green-50'
              }`}
            >
              {verifying
                ? 'Saving…'
                : verifiedAt
                ? 'Unmark Verified'
                : 'Mark as Verified Resolution'}
            </button>
          )}
          <button
            onClick={() => generate(true)}
            className="text-[10px] px-2 py-0.5 text-blue-600 border border-blue-300 rounded hover:bg-blue-50 transition-colors"
          >
            Regenerate
          </button>
        </div>
      </div>
      {verifyError && (
        <p className="text-[10px] text-red-600">{verifyError}</p>
      )}
      <div className="prose prose-sm max-w-none text-xs leading-relaxed">
        <MarkdownRenderer content={summary} />
      </div>
    </div>
  );
}

// ── Full Analysis Result ──────────────────────────────────────────────────

/** Full analysis result display with 4 data tabs + 6 analysis phases. */
const ISSUE_LIMITS = [
  'attachments: documents + images only (.doc not readable)',
  'spreadsheet sheets and rows capped per file',
  'each query returns a limited number of rows',
  'very long cell values are shortened',
];

/** What the analysis actually read. Derived from the result itself, so it can
 *  never claim coverage the stored data does not support. */
function issueCoverage(result: IssueAnalysisResponse): CoverageItem[] {
  const t = result.structured_ticket;
  const items: CoverageItem[] = [];

  // One line per REASON — a file skipped for hitting the attachment cap
  // must not be told to convert its format.
  items.push(...attachmentCoverage(t?.image_extraction_results ?? []));

  const sql = result.retrieval_results?.sql_results ?? [];
  if (sql.length > 0) {
    const empty = sql.filter((r) => !r.data_found).length;
    items.push({
      label: 'Entities with data',
      value: `${sql.length - empty} of ${sql.length}`,
      incomplete: empty > 0,
      detail:
        empty > 0
          ? `${empty} entity(ies) returned no data. That can be a finding, or a table that was never reachable — check the cards before concluding the data is absent.`
          : undefined,
    });
  }

  const gaps = result.retrieval_results?.gaps ?? [];
  if (gaps.length > 0) {
    items.push({
      label: 'Data gaps',
      value: `${gaps.length}`,
      incomplete: true,
      detail: 'Entities the analysis could not query — listed under the Gaps section.',
    });
  }

  const modules = t?.modules ?? [];
  if (modules.length > 0) {
    items.push({ label: 'Modules confirmed', value: `${modules.length}` });
  }
  return items;
}

function AnalysisResult({ result, onReanalyze, loading, userCommentBox, onModuleClick }: { result: IssueAnalysisResponse; onReanalyze: () => void; loading: boolean; userCommentBox?: React.ReactNode; onModuleClick?: (moduleName: string) => void }) {
  const { structured_ticket: ticket, retrieval_results: retrieval, analysis, warnings, duration_ms } = result;
  const [activeTab, setActiveTab] = useState<DataTab>('jira');

  // Shared SQL Investigation cards: seeded from Phase-2 sql_results, then
  // owned at this level so EraxTab and TimelineTab can mirror live edits.
  // Re-seeds whenever a different analysis is loaded (analysis_id change).
  const [sqlCards, setSqlCards] = useState<SqlCard[]>(() => seedCardsFromResult(result));
  useEffect(() => {
    setSqlCards(seedCardsFromResult(result));
  }, [result.analysis_id]);

  // Floating Entity Types / module setup window, opened from the Extracted
  // Entities header in the JIRA tab. Owned here so it outlives tab switches.
  const [entityToolsOpen, setEntityToolsOpen] = useState(false);

  // Tables picked in its Schema Browser tab become starter SELECT cards —
  // same shape the "Browse all" picker inside SQL Investigation produces.
  const handleAddTablesFromTools = useCallback((fqns: string[]) => {
    if (fqns.length === 0) return;
    const now = Date.now();
    setSqlCards((prev) => [
      ...prev,
      ...fqns.map((fqn, i) => ({
        id: `tools-${now}-${i}-${fqn}`,
        label: fqn.split('.').pop() || fqn,
        sql: `SELECT TOP 5 *\nFROM ${fqn}`,
        rows: [] as Record<string, unknown>[],
        rowCount: 0,
        kind: 'data' as const,
        source: 'manual' as const,
        edited: false,
        rationale: 'Added from Entity Types → Schema Browser.',
      })),
    ]);
  }, []);

  // Merge in the server-persisted seeded card set. The refine pane used to
  // show ONLY the analysis row's Phase-2 queries — on MILAHA-2266 that was
  // 4 lone port filters while 15 identifier-anchored seeded cards sat
  // unseen in pre_seeded_cards. Seeded cards are appended after the
  // analysis-derived ones, deduplicated on whitespace/case-insensitive SQL.
  //
  // CURATION GUARD: once the analyst has sent curated evidence
  // (analyze-with-data replaces sql_results with synthetic analyst
  // records), the merge must NOT run — it would resurrect every seeded
  // card the analyst deliberately deleted, and the next Send would ship
  // the junk again. Curation is authoritative; the seed is only merged
  // into never-curated analyses.
  const alreadyCurated = (result.retrieval_results?.sql_results ?? []).some(
    (r) =>
      (r.entity_type === 'data' || r.entity_type === 'timeline') &&
      (r.tables_queried?.length ?? 0) === 0,
  );
  useEffect(() => {
    if (alreadyCurated) return;
    const ticketKey = result.structured_ticket?.ticket_key;
    if (!ticketKey) return;
    const controller = new AbortController();
    issueAnalysisApi
      .getPreCards(ticketKey, controller.signal)
      .then((resp) => {
        if (controller.signal.aborted || resp.cards.length === 0) return;
        const normalize = (sql: string) => sql.split(/\s+/).join(' ').toLowerCase();
        setSqlCards((prev) => {
          const seen = new Set(prev.map((c) => normalize(c.sql)));
          const extras: SqlCard[] = [];
          resp.cards.forEach((c, i) => {
            // `seen` is updated as we go so two identical seeded cards
            // can't both slip in.
            if (!c.sql || seen.has(normalize(c.sql))) return;
            seen.add(normalize(c.sql));
            extras.push({
              id: `seeded-${result.analysis_id ?? 'na'}-${i}`,
              label: c.label || 'untitled',
              sql: c.sql,
              rows: c.rows as Record<string, unknown>[],
              rowCount: c.row_count,
              kind: c.kind === 'timeline' ? 'timeline' : 'data',
              source: 'auto',
              edited: false,
              rationale: c.rationale || undefined,
              error: c.error ?? undefined,
            });
          });
          return extras.length > 0 ? [...prev, ...extras] : prev;
        });
      })
      .catch(() => {
        /* best-effort — the pane still shows the analysis-derived cards */
      });
    return () => controller.abort();
  }, [result.analysis_id, alreadyCurated]);

  // Count badges for tabs
  const eraxCount = (retrieval.sql_results ?? []).filter(r => r.erax_data).length;
  const timelineCount = (retrieval.timelines ?? []).filter(t => t.generated).length;
  const docsCount = (retrieval.document_validations ?? []).length + (retrieval.similar_issues ?? []).length
    + (retrieval.support_docs_answer ? 1 : 0);

  return (
    <div className="space-y-3">
      {/* Header summary */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <span>Analysis for <span className="font-mono font-semibold text-gray-800">{ticket.ticket_key}</span></span>
          {result.cached && (
            <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded border border-blue-200 font-medium">
              Cached
            </span>
          )}
          {result.analyzed_at && (
            <span className="text-[10px] text-gray-400">
              {new Date(result.analyzed_at).toLocaleString()}
            </span>
          )}
          {emailName(result.analyzed_by) && (
            <span
              className="text-[10px] text-gray-500 font-medium"
              title={result.analyzed_by ?? undefined}
            >
              {emailName(result.analyzed_by)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span>{duration_ms}ms</span>
          <button
            onClick={() =>
              downloadAnalysisJson(
                {
                  kind: 'issue-analysis',
                  key: result.structured_ticket?.ticket_key || 'analysis',
                },
                result,
              )
            }
            className="text-[10px] px-2 py-0.5 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            title="Download the full analysis as JSON — entities, SQL evidence and root cause. Contains customer data."
          >
            JSON
          </button>
          {result.cached && (
            <button
              onClick={onReanalyze}
              disabled={loading}
              className="text-[10px] px-2 py-0.5 text-blue-600 border border-blue-300 rounded hover:bg-blue-50 disabled:opacity-50 transition-colors"
            >
              Re-analyze
            </button>
          )}
        </div>
      </div>

      <CoverageNotice items={issueCoverage(result)} limits={ISSUE_LIMITS} />

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="space-y-1">
          {warnings.map((w, i) => (
            <div key={`warn-${i}-${w.slice(0, 30)}`} className="flex items-start gap-2 p-2 bg-amber-50 rounded border border-amber-200 text-xs text-amber-800">
              <AlertCircle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* ═══ DATA TABS ═══ */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Tab bar */}
        <div className="flex border-b border-gray-200 bg-gray-50/50">
          {DATA_TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const badge = tab.id === 'erax' ? eraxCount
              : tab.id === 'timeline' ? timelineCount
              : tab.id === 'docs' ? docsCount
              : null;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors border-b-2 ${
                  isActive
                    ? 'border-blue-500 text-blue-700 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                {tab.icon}
                {tab.label}
                {badge != null && badge > 0 && (
                  <span className="text-[9px] px-1 py-0.5 rounded-full bg-blue-100 text-blue-700 font-mono">{badge}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="p-3">
          {activeTab === 'jira' && (
            <JiraTab
              result={result}
              userCommentBox={userCommentBox}
              cards={sqlCards}
              setCards={setSqlCards}
              onSentToAnalysis={() => setActiveTab('final')}
              onModuleClick={onModuleClick}
              onOpenEntityTools={() => setEntityToolsOpen(true)}
            />
          )}
          {activeTab === 'erax' && <EraxTab result={result} cards={sqlCards} />}
          {activeTab === 'timeline' && <TimelineTab result={result} cards={sqlCards} />}
          {activeTab === 'docs' && <DocsTab result={result} />}
          {activeTab === 'final' && <FinalAnalysisTab result={result} />}
        </div>
      </div>

      {/* Mounted at this level, NOT inside JiraTab: the tab bar unmounts
          whichever tab is inactive, which would kill the window (and any
          half-typed form in it) the moment the analyst went to read the
          ERAX or Timeline tab — the exact thing it exists to allow. */}
      <EntityToolsPanel
        open={entityToolsOpen}
        onClose={() => setEntityToolsOpen(false)}
        onAddTables={handleAddTablesFromTools}
      />

      {/* Data Gaps */}
      {retrieval.gaps?.length > 0 && (
        <div className="space-y-1">
          <span className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide">Data Gaps ({retrieval.gaps.length})</span>
          {retrieval.gaps.map((g) => <GapWarning key={`gap-${g.looked_for.slice(0, 40)}`} gap={g} />)}
        </div>
      )}

      {/* ═══ 6-PHASE ANALYSIS ═══ */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">
          <Target size={14} className="text-green-600" />
          Structured Analysis Pipeline
        </div>

        {/* (a) Structuring the Problem */}
        <Section title="(a) Problem Structuring" icon={<FileText size={14} className="text-blue-500" />} defaultOpen>
          <div className="text-xs">
            <MarkdownRenderer content={analysis.issue_definition} />
          </div>
        </Section>

        {/* (b) Forensic Data Collection */}
        <Section title="(b) Forensic Data Collection" icon={<Database size={14} className="text-purple-500" />}>
          <div className="space-y-2 text-xs">
            {analysis.information_inventory.sql_data_summary?.length > 0 && (
              <div>
                <span className="text-[10px] font-semibold text-gray-500">SQL Data Extracted</span>
                <ul className="pl-3 list-disc text-gray-600">
                  {analysis.information_inventory.sql_data_summary.map((s, i) => <li key={`sql-sum-${i}-${s.slice(0, 20)}`}>{s}</li>)}
                </ul>
              </div>
            )}
            {analysis.information_inventory.timelines_generated?.length > 0 && (
              <div>
                <span className="text-[10px] font-semibold text-gray-500">Timelines</span>
                <ul className="pl-3 list-disc text-gray-600">
                  {analysis.information_inventory.timelines_generated.map((t, i) => <li key={`tl-sum-${i}-${t.slice(0, 20)}`}>{t}</li>)}
                </ul>
              </div>
            )}
            {analysis.information_inventory.documents_matched?.length > 0 && (
              <div>
                <span className="text-[10px] font-semibold text-gray-500">Documents Matched</span>
                <ul className="pl-3 list-disc text-gray-600">
                  {analysis.information_inventory.documents_matched.map((d, i) => <li key={`doc-${i}-${d.slice(0, 20)}`}>{d}</li>)}
                </ul>
              </div>
            )}
            {analysis.information_inventory.past_issues_matched?.length > 0 && (
              <div>
                <span className="text-[10px] font-semibold text-gray-500">Past Issues</span>
                <ul className="pl-3 list-disc text-gray-600">
                  {analysis.information_inventory.past_issues_matched.map((p, i) => <li key={`past-${i}-${p.slice(0, 20)}`}>{p}</li>)}
                </ul>
              </div>
            )}
            {analysis.information_inventory.gaps?.length > 0 && (
              <div className="space-y-1">
                <span className="text-[10px] font-semibold text-amber-600">Gaps</span>
                {analysis.information_inventory.gaps.map((g) => <GapWarning key={`inv-gap-${g.looked_for.slice(0, 40)}`} gap={g} />)}
              </div>
            )}
          </div>
        </Section>

        {/* (c) Data Analysis */}
        <Section title="(c) Data Analysis" icon={<Search size={14} className="text-indigo-500" />}>
          <div className="space-y-2 text-xs">
            <p className="text-gray-600 italic">
              Validation errors, constraint violations, and anomalies found across SQL data, timelines, documents, and knowledge bank.
            </p>
            {analysis.differential_diagnosis?.length > 0 && (
              <div>
                <span className="text-[10px] font-semibold text-gray-500">Evidence collected from {analysis.differential_diagnosis.length} hypothesis paths:</span>
                <ul className="pl-3 list-disc text-gray-600 mt-1">
                  {Array.from(new Set(analysis.differential_diagnosis.flatMap(h => h.supporting_evidence))).slice(0, 10).map((e, i) => (
                    <li key={`ev-${i}-${e.slice(0, 20)}`}>{e}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Section>

        {/* (d) Hypothesis Generation */}
        <Section
          title="(d) Hypothesis Generation"
          icon={<Lightbulb size={14} className="text-amber-500" />}
          badge={<span className="text-[10px] text-gray-400">{analysis.differential_diagnosis?.length ?? 0} hypotheses</span>}
          defaultOpen
        >
          <div className="space-y-1">
            {analysis.differential_diagnosis?.map((h, i) => (
              <HypothesisCard key={`hyp-${i}-${h.confidence_score}`} h={h} index={i} />
            ))}
          </div>
        </Section>

        {/* (e) Root Cause Analysis */}
        <Section title="(e) Root Cause Analysis" icon={<Target size={14} className="text-red-500" />} defaultOpen>
          <div className={`p-2 rounded text-xs ${analysis.root_cause_analysis.determined ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              {analysis.root_cause_analysis.determined
                ? <CheckCircle size={14} className="text-green-600" />
                : <AlertTriangle size={14} className="text-amber-600" />
              }
              <span className="font-semibold text-gray-800">
                {analysis.root_cause_analysis.determined ? 'Root Cause Identified' : 'Root Cause Not Yet Determined'}
              </span>
            </div>
            <p className="text-gray-700 leading-relaxed">{analysis.root_cause_analysis.causal_chain}</p>
            {analysis.root_cause_analysis.supporting_data_points?.length > 0 && (
              <div className="mt-2">
                <span className="text-[10px] font-semibold text-green-600">Supporting Data Points:</span>
                <ul className="pl-3 list-disc text-gray-600 mt-1">
                  {analysis.root_cause_analysis.supporting_data_points.map((p, i) => <li key={`sdp-${i}-${p.slice(0, 20)}`}>{p}</li>)}
                </ul>
              </div>
            )}
            {analysis.root_cause_analysis.additional_info_required?.length > 0 && (
              <div className="mt-2">
                <span className="text-[10px] font-semibold text-gray-500">Additional Information Required:</span>
                <ul className="pl-3 list-disc text-gray-600 mt-1">
                  {analysis.root_cause_analysis.additional_info_required.map((a, i) => <li key={`addl-${i}-${a.slice(0, 20)}`}>{a}</li>)}
                </ul>
              </div>
            )}
          </div>
        </Section>

        {/* Applicable SQL Scripts — temporary fixes for the identified root cause */}
        {/* Note: Shows independently of RCA causal_chain to display available scripts or "None found" message */}
        <ApplicableSqlScripts
          applicableScripts={result.retrieval_results.applicable_scripts || []}
          rootCauseAnalysis={analysis.root_cause_analysis}
          issueId={result.analysis_id || 0}
          extractedEntities={result.structured_ticket.entities.map(e => ({ entity_type: e.entity_type, value: e.value, confidence: e.confidence === 'high' ? 0.95 : e.confidence === 'medium' ? 0.7 : 0.5 })) || []}
          detectedModules={result.structured_ticket.detected_modules?.map(m => m.name) || []}
        />

        {/* Layered RCA (Layers 1, 3, 5) — plain language explanation for new support staff */}
        <LayeredRcaCards layeredRca={analysis.layered_rca || null} />

        {/* Compass — structural view from the LRP code + schema graph (external
            read-only service, button-triggered; see docs/flow/COMPASS.md) */}
        <CompassImpactPanel
          issueText={buildTicketText(result.structured_ticket) || result.structured_ticket.summary || ''}
          ticketKey={result.structured_ticket.ticket_key}
          modules={result.structured_ticket.modules ?? []}
          entities={result.structured_ticket.entities ?? []}
          analysisId={result.analysis_id}
          dataContext={buildDataContextWithEvidence(
            sqlCards,
            result.structured_ticket.extracted_image_text,
            result.structured_ticket.extracted_document_text,
            result.retrieval_results?.document_validations,
          )}
          hints={buildHints(sqlCards, result.structured_ticket.modules)}
        />

        <CompassSearchBrowse modules={result.structured_ticket.modules ?? []} />

        {/* RCA Visualizations — Gantt timeline and causal chain flowcharts */}
        {result.analysis_id && <RcaVisualizationPanel analysisId={result.analysis_id} />}

        {/* Analyst Final Comments — sits between RCA and Resolution so the
            analyst's note shows up alongside their root-cause conclusion. */}
        {result.analysis_id != null && (
          <AnalystCommentBox
            analysisId={result.analysis_id}
            initialComment={result.analyst_comment ?? null}
            initialUpdatedAt={result.analyst_comment_updated_at ?? null}
            initialUpdatedBy={result.analyst_comment_updated_by ?? null}
          />
        )}

        {/* (f) Resolution & Fix Planning */}
        <Section title="(f) Resolution & Fix Planning" icon={<Wrench size={14} className="text-green-600" />} defaultOpen>
          <div className="space-y-2">
            {analysis.resolution_plan.immediate_workaround && (
              <div className="p-2 bg-blue-50 rounded border border-blue-200 text-xs">
                <div className="flex items-center gap-2 mb-1">
                  <Wrench size={14} className="text-blue-600" />
                  <span className="font-semibold text-blue-800">Immediate Workaround</span>
                </div>
                <ol className="pl-4 list-decimal text-gray-700 space-y-0.5">
                  {analysis.resolution_plan.immediate_workaround.steps?.map((s, i) => <li key={`wa-${i}-${s.slice(0, 20)}`}>{s}</li>)}
                </ol>
              </div>
            )}

            {analysis.resolution_plan.permanent_fix && (
              <div className="p-2 bg-green-50 rounded border border-green-200 text-xs">
                <div className="flex items-center gap-2 mb-1">
                  <Shield size={14} className="text-green-600" />
                  <span className="font-semibold text-green-800">Permanent Fix</span>
                </div>
                <p className="text-gray-700">{analysis.resolution_plan.permanent_fix.description}</p>
                <div className="mt-1 grid grid-cols-2 gap-1 text-[10px] text-gray-500">
                  <span>Module: {analysis.resolution_plan.permanent_fix.module_affected}</span>
                  <span>Effort: {analysis.resolution_plan.permanent_fix.effort_estimate}</span>
                </div>
                {analysis.resolution_plan.permanent_fix.escalation_path && (
                  <p className="mt-1 text-amber-700 italic">
                    Escalation: {analysis.resolution_plan.permanent_fix.escalation_path}
                  </p>
                )}
              </div>
            )}

            {analysis.resolution_plan.knowledge_bank_reference && (
              <div className="p-2 bg-purple-50 rounded border border-purple-200 text-xs">
                <div className="flex items-center gap-2">
                  <BookOpen size={14} className="text-purple-600" />
                  <span className="text-purple-800">{analysis.resolution_plan.knowledge_bank_reference}</span>
                </div>
              </div>
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}
